import { useTranslation } from 'react-i18next';
import { useEffect, useState, useRef } from 'react';
import Header from '../components/Header';
import { COUNTRY_DIAL_CODES, DEFAULT_COUNTRY } from '../data/countryDialCodes';

const Home = () => {
    const [selectedCountry, setSelectedCountry] = useState(DEFAULT_COUNTRY);
    const [countryPickerOpen, setCountryPickerOpen] = useState(false);
    const [countrySearch, setCountrySearch] = useState('');
    const countryPickerRef = useRef(null);
    const countrySearchRef = useRef(null);
    const countryListRef = useRef(null);

    const filteredCountries = COUNTRY_DIAL_CODES.filter((country) => {
        const query = countrySearch.trim().toLowerCase();
        if (!query) return true;
        return (
            country.name.toLowerCase().includes(query) ||
            country.iso.toLowerCase().includes(query) ||
            country.code.replace('+', '').includes(query.replace('+', '')) ||
            country.code.includes(query)
        );
    });

    function toggleSelection(button) {
        const buttons = document.querySelectorAll('.button-sec .btn');
        buttons.forEach(btn => btn.classList.remove('btn-selected'));

        button.classList.add('btn-selected');

        const selectedProgram = button.textContent.trim();
        const hiddenInput = document.getElementById('selectedProgram');
        if (hiddenInput) {
            hiddenInput.value = selectedProgram;
        }
    }

    function applyCountryToMobile(country) {
        const mobile = document.getElementById('mobile');
        if (!mobile) return;

        mobile.setAttribute('maxlength', String(country.max));
        mobile.removeAttribute('minlength');
        mobile.value = mobile.value.replace(/\D/g, '').slice(0, country.max);

        if (window.$ && window.$.fn && window.$.fn.validate && window.$('#mobile').length) {
            window.$('#mobile').valid();
        }
    }

    function handleCountrySelect(country) {
        setSelectedCountry(country);
        setCountryPickerOpen(false);
        setCountrySearch('');
        applyCountryToMobile(country);
    }

    function handleDropdownScrollLock(event) {
        event.stopPropagation();
    }

    function handleCountryListWheel(event) {
        event.stopPropagation();
        const list = countryListRef.current;
        if (!list) return;

        const { scrollTop, scrollHeight, clientHeight } = list;
        const atTop = scrollTop <= 0 && event.deltaY < 0;
        const atBottom = scrollTop + clientHeight >= scrollHeight - 1 && event.deltaY > 0;

        if (atTop || atBottom) {
            event.preventDefault();
        }
    }

    useEffect(() => {
        function handleFormReset() {
            setSelectedCountry(DEFAULT_COUNTRY);
            setCountryPickerOpen(false);
            setCountrySearch('');
            applyCountryToMobile(DEFAULT_COUNTRY);
        }

        window.addEventListener('contactFormReset', handleFormReset);
        return () => window.removeEventListener('contactFormReset', handleFormReset);
    }, []);

    useEffect(() => {
        function handleClickOutside(event) {
            if (countryPickerRef.current && !countryPickerRef.current.contains(event.target)) {
                setCountryPickerOpen(false);
                setCountrySearch('');
            }
        }

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        if (countryPickerOpen && countrySearchRef.current) {
            countrySearchRef.current.focus();
        }

        if (window.locoScroll) {
            if (countryPickerOpen && typeof window.locoScroll.stop === 'function') {
                window.locoScroll.stop();
            } else if (!countryPickerOpen && typeof window.locoScroll.start === 'function') {
                window.locoScroll.start();
            }
        }

        return () => {
            if (window.locoScroll && typeof window.locoScroll.start === 'function') {
                window.locoScroll.start();
            }
        };
    }, [countryPickerOpen]);

    useEffect(() => {
        const list = countryListRef.current;
        if (!countryPickerOpen || !list) return undefined;

        const onWheel = (event) => handleCountryListWheel(event);
        const onTouchMove = (event) => event.stopPropagation();

        list.addEventListener('wheel', onWheel, { passive: false });
        list.addEventListener('touchmove', onTouchMove, { passive: true });

        return () => {
            list.removeEventListener('wheel', onWheel);
            list.removeEventListener('touchmove', onTouchMove);
        };
    }, [countryPickerOpen, filteredCountries.length]);

    useEffect(() => {
        // Load Google reCAPTCHA script immediately
        const script = document.createElement('script');
        script.src = 'https://www.google.com/recaptcha/api.js';
        script.async = true;
        script.defer = true;

        // Define callbacks globally
        window.recaptchaCallback = function () {
            console.log('reCAPTCHA verified');
            const errorMsg = document.getElementById('captcha-error');
            if (errorMsg) errorMsg.style.display = 'none';

            // Update scroll after reCAPTCHA loads to prevent layout issues
            if (window.locoScroll && typeof window.locoScroll.update === 'function') {
                setTimeout(() => {
                    window.locoScroll.update();
                }, 100);
            } else {
                console.log('Locomotive Scroll not available, retrying...');
                // Retry if scroll is not ready yet
                setTimeout(() => {
                    if (window.locoScroll && typeof window.locoScroll.update === 'function') {
                        window.locoScroll.update();
                    }
                }, 500);
            }
        };

        // Append script to head
        document.head.appendChild(script);

        // Cleanup function
        return () => {
            if (document.head.contains(script)) {
                document.head.removeChild(script);
            }
        };
    }, []);

    const { t } = useTranslation();
    return (
        <>
            <div id="loading2">
                <div class="load"></div>
            </div>
            <div>
                <div class="preloader">
                    <svg class="progress-icon mb-4" width="506" height="663" viewBox="0 0 506 863" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M38.6618 565.214C41.0271 568.967 43.4976 572.695 46.0732 576.396C66.0472 605.035 90.7257 628.319 119.399 645.592C135.273 655.147 151.804 662.996 168.808 669.034C182.265 673.838 196.036 677.513 210.018 680.059C213.986 680.794 218.008 681.424 222.002 681.923L230.491 695.022L339.061 862.604H231.464L83.2354 634.725L46.5988 578.365L38.6355 565.187L38.6618 565.214Z" fill="#8DA6FF" />
                        <path d="M392.334 348.255C389.916 344.134 387.34 340.091 384.555 336.101L384.266 335.681L284.001 180.883C278.876 180.2 273.751 179.701 268.6 179.334C245.551 177.785 222.266 179.439 199.059 184.295C166.575 191.12 136.088 204.062 108.414 222.726L107.73 223.198C81.1333 243.411 58.9779 267.22 41.816 294.1C42.6308 293.943 43.4455 293.812 44.2602 293.733C45.6269 293.575 47.0461 293.497 48.439 293.497C59.819 293.497 70.6207 298.983 77.2962 308.171L106.916 348.832C108.335 350.801 109.57 352.875 110.569 355.054C112.093 352.245 113.696 349.462 115.405 346.706C126.443 328.856 141.318 313.263 159.663 300.374C177.981 287.511 197.771 278.875 218.534 274.728C239.585 270.528 260.637 270.081 281.084 273.415C301.373 276.723 320.769 283.994 338.667 295.045C341.61 296.857 344.475 298.773 347.287 300.794C361.611 311.058 374.278 323.947 385.054 339.251" fill="#8DA6FF" />
                        <path d="M505.974 427.161C505.974 426.374 505.974 425.56 505.921 424.746C505.921 423.381 505.842 421.99 505.79 420.599C505.737 419.496 505.685 418.394 505.632 417.317C505.58 416.504 505.527 415.664 505.474 414.85C505.422 414.036 505.369 413.196 505.317 412.382C505.264 411.464 505.185 410.545 505.106 409.626C504.187 398.496 502.478 387.34 500.034 376.209C492.885 343.79 479.193 312.92 459.35 284.438L274.538 0.199219H166.941L283.999 180.907L384.264 335.705L384.553 336.125C397.457 354.448 406.235 374.451 410.624 395.608C415.066 416.95 415.644 438.134 412.306 458.557C410.624 468.926 407.864 479.085 404.106 488.902C400.532 498.274 396.038 507.356 390.624 516.098C379.585 533.948 364.71 549.541 346.365 562.43C328.047 575.292 308.257 583.929 287.495 588.076C266.443 592.276 245.391 592.722 224.944 589.389C204.655 586.081 185.259 578.81 167.361 567.758C149.49 556.733 133.879 541.849 120.948 523.553C108.07 505.282 99.4496 485.49 95.2709 464.752C91.0658 443.804 90.7767 422.883 94.4036 402.591C96.3747 391.435 98.6086 384.741 100.107 381.066C101.053 378.782 101.631 376.393 101.868 373.978C102.525 367.704 100.869 361.247 97.058 355.997L67.4649 315.335C62.4451 308.457 54.0876 304.782 45.6249 305.806C39.3699 306.541 33.7719 309.717 29.961 314.495C28.5944 316.175 27.4643 318.091 26.597 320.139C25.6771 322.37 24.7047 324.68 23.7323 327.095C17.3984 342.714 10.3812 361.719 6.22874 378.204C6.22874 378.257 6.20246 378.336 6.17618 378.362C5.30889 382.09 4.54672 385.817 3.8634 389.571C2.04997 399.572 0.867293 409.652 0.315379 419.785C0.236535 421.229 0.15769 422.673 0.131408 424.116C0.131408 424.274 0.131408 424.405 0.131408 424.563C0.0788449 426.243 0.0262816 427.949 0 429.629C0 431.65 0 433.645 0 435.667C0 436.533 0 437.373 0.0525632 438.239C0.0525632 439.578 0.131408 440.917 0.183971 442.229C0.210253 443.148 0.262816 444.067 0.315379 444.985C0.367943 445.825 0.420506 446.639 0.473069 447.479C1.23524 459.974 2.96982 472.443 5.62427 484.886C12.5363 517.043 26.1239 547.808 46.0717 576.395C66.0457 605.034 90.7242 628.318 119.397 645.591C135.271 655.146 151.803 662.995 168.807 669.032C182.263 673.836 196.035 677.511 210.016 680.057C213.985 680.792 218.006 681.422 222.001 681.921C250.017 685.596 278.533 684.467 306.943 678.509C339.427 671.684 369.914 658.742 397.588 640.078L398.272 639.579C426.314 618.264 449.442 592.932 466.972 564.267C470.178 559.043 473.174 553.741 476.013 548.386C478.194 544.238 480.244 540.064 482.189 535.864C488.549 520.166 495.619 501.03 499.771 484.492C499.798 484.413 499.824 484.308 499.85 484.203C500.691 480.581 501.427 476.932 502.11 473.257C503.819 463.912 504.949 454.514 505.553 445.038C505.632 443.673 505.711 442.334 505.763 440.969C505.79 440.077 505.842 439.158 505.869 438.265C505.921 436.559 505.974 434.879 506 433.173C506 431.151 506 429.13 506 427.135L505.974 427.161Z" fill="#fff" />
                    </svg>

                    <div class="progress-container">
                        <div class="progress-bar"></div>
                    </div>
                    <div class="text-container">
                        <div class="loading-text initial">Loading</div>
                        <div class="loading-text complete">Complete</div>
                    </div>
                    <div class="percentage">0</div>
                </div>
            </div>
            <Header />
            <div class="smooth-scroll" data-scroll-container>
                <div class="home-section-1" id="home-id">
                    <div class="section-1-1">
                        <div class="hero">
                            <div class="logo" id="heroLogo">
                                <svg width="1156" height="240" viewBox="0 0 1156 240" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M10.7593 157.239C11.4175 158.284 12.105 159.321 12.8218 160.351C18.3804 168.321 25.2482 174.801 33.2278 179.608C37.6454 182.267 42.2459 184.451 46.978 186.131C50.7228 187.468 54.5553 188.491 58.4463 189.2C59.5507 189.404 60.6698 189.579 61.7815 189.718L64.1439 193.364L94.3579 240H64.4145L23.1638 176.583L12.9681 160.899L10.752 157.232L10.7593 157.239Z" fill="#8DA6FF" />
                                    <path d="M109.183 96.861C108.511 95.7141 107.794 94.5891 107.019 93.4787L106.938 93.3618L79.0353 50.2827C77.6091 50.0927 76.1828 49.9539 74.7493 49.8516C68.3349 49.4206 61.8548 49.8809 55.3965 51.2323C46.3565 53.1317 37.8723 56.7332 30.1707 61.9273L29.9805 62.0587C22.5788 67.6838 16.4131 74.3097 11.6371 81.7903C11.8638 81.7464 12.0906 81.7099 12.3173 81.688C12.6976 81.6442 13.0926 81.6223 13.4802 81.6223C16.6472 81.6223 19.6532 83.1491 21.5109 85.7059L29.7538 97.0218C30.1487 97.5697 30.4925 98.1468 30.7704 98.7531C31.1946 97.9714 31.6408 97.1971 32.1162 96.43C35.1881 91.4625 39.3278 87.1231 44.4329 83.5362C49.5307 79.9567 55.0382 77.5532 60.8162 76.399C66.6747 75.2302 72.5332 75.106 78.2234 76.0337C83.8698 76.9542 89.2675 78.9778 94.2483 82.0533C95.0675 82.5573 95.8647 83.0906 96.6473 83.6531C100.633 86.5095 104.159 90.0964 107.157 94.3553" fill="#8DA6FF" />
                                    <path d="M140.808 118.82C140.808 118.601 140.808 118.375 140.794 118.148C140.794 117.768 140.772 117.381 140.757 116.994C140.743 116.687 140.728 116.38 140.713 116.081C140.699 115.854 140.684 115.621 140.67 115.394C140.655 115.168 140.64 114.934 140.626 114.707C140.611 114.452 140.589 114.196 140.567 113.94C140.311 110.843 139.836 107.738 139.156 104.641C137.166 95.6187 133.356 87.0277 127.834 79.1015L76.4017 0H46.4583L79.0347 50.2895L106.938 93.3686L107.018 93.4855C110.609 98.5846 113.052 104.151 114.273 110.039C115.509 115.978 115.67 121.874 114.742 127.557C114.273 130.443 113.505 133.27 112.46 136.002C111.465 138.61 110.214 141.138 108.707 143.57C105.636 148.538 101.496 152.877 96.3908 156.464C91.2929 160.044 85.7855 162.447 80.0075 163.602C74.149 164.77 68.2905 164.895 62.6002 163.967C56.9539 163.046 51.5562 161.023 46.5753 157.947C41.6018 154.879 37.2573 150.737 33.6589 145.645C30.075 140.561 27.6761 135.053 26.5131 129.281C25.3429 123.452 25.2624 117.629 26.2718 111.982C26.8203 108.878 27.442 107.015 27.8589 105.992C28.1222 105.357 28.2831 104.692 28.3489 104.02C28.5318 102.274 28.071 100.477 27.0105 99.0156L18.775 87.6998C17.378 85.7858 15.0521 84.763 12.697 85.0479C10.9563 85.2525 9.39845 86.1364 8.33792 87.466C7.9576 87.9335 7.6431 88.4668 7.40173 89.0366C7.14575 89.6576 6.87513 90.3004 6.60451 90.9725C4.84185 95.3192 2.88902 100.608 1.73341 105.196C1.73341 105.21 1.7261 105.232 1.71878 105.24C1.47742 106.277 1.26532 107.314 1.07515 108.359C0.570489 111.142 0.241361 113.948 0.0877676 116.767C0.0658257 117.169 0.0438838 117.571 0.0365698 117.973C0.0365698 118.017 0.0365698 118.053 0.0365698 118.097C0.0219419 118.565 0.00731397 119.039 0 119.507C0 120.069 0 120.625 0 121.187C0 121.428 0 121.662 0.0146279 121.903C0.0146279 122.276 0.0365698 122.648 0.0511978 123.013C0.0585117 123.269 0.0731397 123.525 0.0877676 123.78C0.102396 124.014 0.117023 124.241 0.131651 124.474C0.343756 127.952 0.826478 131.422 1.56519 134.884C3.48876 143.833 7.27008 152.395 12.8214 160.351C18.38 168.321 25.2478 174.8 33.2274 179.607C37.645 182.266 42.2455 184.451 46.9776 186.131C50.7224 187.468 54.5549 188.491 58.4459 189.199C59.5503 189.404 60.6694 189.579 61.7811 189.718C69.5778 190.741 77.5134 190.426 85.4198 188.768C94.4599 186.869 102.944 183.267 110.646 178.073L110.836 177.934C118.64 172.003 125.076 164.953 129.955 156.976C130.847 155.522 131.681 154.046 132.471 152.556C133.078 151.402 133.648 150.24 134.189 149.071C135.959 144.703 137.927 139.377 139.082 134.775C139.09 134.753 139.097 134.724 139.104 134.695C139.338 133.686 139.543 132.671 139.733 131.648C140.209 129.048 140.523 126.432 140.691 123.795C140.713 123.415 140.735 123.043 140.75 122.663C140.757 122.414 140.772 122.159 140.779 121.91C140.794 121.435 140.808 120.968 140.816 120.493C140.816 119.931 140.816 119.368 140.816 118.813L140.808 118.82Z" fill="#243CE6" />
                                    <path d="M200.067 155.581C211.484 165.713 224.802 171.63 237.697 171.63C250.591 171.63 258.629 166.137 259.895 157.063C261.16 148.407 255.667 140.802 240.023 132.569L234.106 129.398C209.582 116.519 198.801 100.47 201.968 79.9856C205.347 55.9147 225.856 40.0769 253.554 40.0769C271.524 40.0769 292.669 46.8343 303.662 55.9147L285.055 79.9856C274.91 72.5927 263.281 68.3702 253.129 68.3702C241.924 68.3702 234.106 73.6519 232.833 81.6731C231.568 89.6943 237.485 97.0872 254.395 106.38L262.009 110.39C284.419 122.634 294.358 138.472 291.184 159.167C287.593 184.509 266.66 199.924 236.636 199.924C215.075 199.924 194.142 192.107 180.823 179.228L200.059 155.581H200.067Z" fill="#2B2B2B" />
                                    <path d="M418.214 199.932H392.052L394.165 187.914C384.24 195.505 371.997 199.932 359.117 199.932C327.023 199.932 304.43 172.515 309.499 139.196C313.931 108.828 339.903 86.057 370.307 86.057C385.718 86.057 398.393 91.9597 407.257 101.661L408.947 89.2202H435.343L418.221 199.932H418.214ZM365.239 174.21C382.339 174.21 396.703 161.557 399.234 144.054C401.977 125.703 390.362 111.786 372.626 111.786C354.889 111.786 341.161 124.439 338.63 141.942C335.888 160.293 347.502 174.21 365.239 174.21Z" fill="#2B2B2B" />
                                    <path d="M454.812 89.2199H483.497L466.763 199.931H438.078L454.812 89.2199ZM475.035 40.5011C485.589 40.5011 492.771 48.0913 491.294 58.2164C490.029 67.4941 481.793 74.2441 471.868 74.2441C461.314 74.2441 454.344 66.654 456.033 56.5289C457.299 47.0393 465.322 40.5011 475.035 40.5011Z" fill="#2B2B2B" />
                                    <path d="M508.064 40.0775H537.006L528.675 94.3849C535.909 88.2557 545.556 84.5301 556.3 84.5301C582.608 84.5301 599.708 106.212 595.539 134.016L585.673 199.924H556.731L566.378 135.543C568.573 121.093 560.681 111.019 547.523 111.019C535.243 111.019 525.164 119.778 522.97 132.701L512.884 199.924H483.943L508.057 40.0702L508.064 40.0775Z" fill="#2B2B2B" />
                                    <path d="M717.449 199.931H690.541L692.515 187.921C682.59 195.511 670.347 199.946 657.467 199.946C625.373 199.946 602.78 172.529 607.849 139.21C612.281 108.842 638.253 86.0638 668.657 86.0638C684.068 86.0638 696.743 91.9665 705.607 101.668L707.297 89.227H733.693L717.449 199.938V199.931ZM663.596 174.209C680.696 174.209 695.061 161.556 697.591 144.053C700.334 125.702 688.727 111.786 670.983 111.786C653.239 111.786 639.518 124.438 636.988 141.942C634.245 160.293 645.852 174.209 663.596 174.209Z" fill="#2B2B2B" />
                                    <path d="M748.27 89.2204H768.333L772.977 58.6405H800.85L796.206 89.2204H819.647L816.056 113.474L792.41 113.686L779.479 199.932H751.606L764.537 113.474H744.687L748.278 89.2204H748.27Z" fill="#2B2B2B" />
                                    <path d="M930.446 199.931H904.071L905.79 187.921C895.865 195.511 883.622 199.946 870.742 199.946C838.648 199.946 816.055 172.529 821.124 139.21C825.556 108.842 851.528 86.0638 881.932 86.0638C897.343 86.0638 910.018 91.9665 918.882 101.668L920.572 89.227H946.968L930.453 199.938L930.446 199.931ZM876.864 174.209C893.964 174.209 908.328 161.556 910.859 144.053C913.602 125.702 901.994 111.786 884.251 111.786C866.507 111.786 852.786 124.438 850.255 141.942C847.513 160.293 859.12 174.209 876.864 174.209Z" fill="#2B2B2B" />
                                    <path d="M962.378 163.873C970.826 171.887 979.478 176.314 987.509 176.314C994.479 176.314 998.487 173.15 999.336 167.664C1000.18 162.39 997.434 159.227 986.456 153.324L982.441 151.001C964.28 141.088 957.317 130.758 959.636 116.199C962.166 97.8477 977.584 86.0424 999.328 86.0424C1013.47 86.0424 1028.04 91.1049 1036.49 98.9069L1021.5 118.733C1014.53 112.619 1007.98 109.668 1001.02 109.668C994.055 109.668 988.979 112.831 988.35 117.893C987.509 122.956 991.305 126.959 1002.92 132.657L1006.09 134.345C1023.82 143.199 1030.79 154.383 1028.47 169.563C1025.72 188.331 1010.94 199.931 989.616 199.931C972.72 199.931 954.779 192.758 946.119 182.428L962.378 163.865V163.873Z" fill="#2B2B2B" />
                                    <path d="M1065.28 40.077H1092.32L1078.51 132.24L1118.63 92.3828H1155.79L1103.85 142.577L1141.22 199.946H1107.86L1075.98 149.539L1068.38 199.946H1040.51L1065.29 40.0843L1065.28 40.077Z" fill="#2B2B2B" />
                                </svg>
                            </div>
                        </div>
                        <div class="row">
                            <div class="col-lg-6">
                                <div class="content-1">
                                    <div class="content-1-1">
                                        <div class="para-1">
                                            <div class="img-div">
                                                <img src="assets/images/Ellipse-1.png" alt="Ellipse-1" class="Ellipse-1-img" />
                                            </div>
                                            <div class="img-div-2">
                                                <img src="assets/images/Ellipse-2.png" alt="Ellipse-2" class="Ellipse-2-img" />
                                            </div>

                                        </div>

                                        <div class="para-2 transforming-p textify1-1 live-animation1">
                                            <p>Simplifying Work.</p>
                                        </div>
                                    </div>

                                    <div class="content-1-2 textify1-2 live-animation1">
                                        <p>Empowering Teams.</p>
                                    </div>

                                    <div class="content-1-3">
                                        <div class="para-1 textify1-3 live-animation1">
                                            <p>Managing Productivity.</p>
                                        </div>
                                        <div class="para-2 live-animation1">
                                            <div class="arrow-div1">
                                                <img src="assets/images/right-arrow.svg" alt="right-arrow" />
                                            </div>
                                            <div class="arrow-div2">
                                                <img src="assets/images/right-arrow-2.svg" alt="right-arrow-2" />
                                            </div>
                                            <div class="arrow-div3">
                                                <img src="assets/images/right-arrow-2.svg" alt="right-arrow-3" />
                                            </div>
                                        </div>
                                    </div>

                                    <div class="content-1-4">
                                        <div class="para-1 fade-right-3 live-animation1">
                                            {/* <p class="mb-3">Spreading Lives to the Lives</p> */}
                                            <p>Saihatask is your complete task, project, and team <br></br> management platform — the best task and
                                                project management software <br></br> helping organizations deliver projects on time with full control.</p>
                                        </div>
                                    </div>

                                    <div class="content-1-5">
                                        <div class="para-1">
                                            <button id="donate2-now" class="donate-btn btn-shake"><a href="#contact-id">Book Demo</a></button>
                                            <button class="arrow-btn btn-shake"><img src="assets/images/right-arrow-2.svg"
                                                alt="right-arrow-4" /></button>
                                            <button id="donate2-now" class="donate-btn donate-btn123 btn-shake"><a href="#contact-id">Start Free Trial</a></button>
                                        </div>
                                    </div>
                                </div>

                            </div>
                            <div class="col-lg-6">
                                {/* <div class="content-2">
                                    <div class="content-2-1">
                                        <div class="card-1">
                                            <div class="img-div">
                                                <img src="assets/images/section-1-card-1.jpg" alt=""
                                                    class="img-fluid card-1-img" />
                                                <div class="content-1-1-1">
                                                    <div class="img-div2">
                                                        <img src="assets/images/logo-1.png" alt="" class="program21" />
                                                    </div>

                                                    <p class="para-1 textify1-1">300+ </p>
                                                    <p class="para-2 fade-right-4"> Projects <br></br> Completed</p>
                                                </div>

                                                <div class="content-2-1-1">
                                                    <p class="para-1 fade-right-4"> 50+ Partnerships <br></br> for Change</p>
                                                </div>
                                            </div>
                                        </div>

                                        <div class="card-2">

                                            <p class="fade-right-4">Creating sustainable livelihoods for rural areas.</p>
                                        </div>
                                    </div>

                                    <div class="content-2-2">
                                        <div class="card-3">
                                            <p class="fade-right-4">Empowering over 5,000 children through access to quality
                                                education.</p>
                                        </div>

                                        <div class="card-4">
                                            <div class="img-div">
                                                <img src="assets/images/section-1-card-4.jpg" alt=""
                                                    class="img-fluid card-4-img" />
                                                <button class="arrow-btn2"><img src="assets/images/right-arrow-3.svg"
                                                    alt="" /></button>
                                                <p class="fade-right-4">Providing medical
                                                    aid to underserved
                                                    communities.</p>
                                            </div>
                                        </div>
                                    </div>
                                </div> */}
                                <div class="main-div-home position-relative">
                                    <img src="assets/images/home-main-img.png" alt="home-main-img"
                                        class="img-fluid" />
                                    <img src="assets/images/home-sub-img1.png" alt="home-sub-img1"
                                        class="img-fluid img-12" />
                                    <img src="assets/images/home-sub-img2.png" alt="home-sub-img2"
                                        class="img-fluid img-123" />
                                </div>
                            </div>
                        </div>
                    </div>


                </div>

                <div class="home-section-2" id="About-saiha">
                    <div class="section-2">
                        <div class="content-1">
                            <div class="round-ball">
                                <img src="assets/images/aboutSaihatask.png" alt="Saihatasklogo" class="img-fluid hand-in-circle" />
                            </div>
                            <p>about Saihatask</p>
                        </div>

                        <div class="content-2 big-text-main">
                            <p class="big-text">Empowering</p>
                        </div>

                        <div class="content-3 textify-1">
                            <p class="para-1 lives-para1">Teams,</p>

                            <div class="para-2  up-div">
                                <p>Since 2022, Saihatask's best task and project management software has helped businesses automate task management, improve team collaboration, and achieve operational excellence</p>
                            </div>
                        </div>

                        <div class="content-4 textify-1">
                            <p class="big-text down-div">Driving</p>
                        </div>

                        <div class="content-5 textify-1">
                            <p class="big-text down-div"> Productivity.</p>

                            <div class="curve-card-1">
                                <div class="para-1">
                                    <p class="para-1-1">10+</p>
                                    <p class="para-1-2">SaaS <br></br> Modules</p>
                                </div>
                            </div>

                            <div class="curve-card-2">
                                <div class="para-2">
                                    <p class="para-1-1">100%</p>
                                    <p class="para-1-2"> Data
                                        <br></br>Visibility</p>
                                </div>
                            </div>
                        </div>


                    </div>

                    <div class="circle-div">
                        <div class="text-div"> <img src="assets/images/circle-with-text.png" alt="circle-with-text-img" class="img-fluid program21" />
                            <div class="img-div">
                                <img src="assets/images/logo-2.png" alt="logo" class="img-fluid hand-in-circle" />
                            </div>
                        </div>

                    </div>
                </div>
                <div class="home-section-3" id="about-id">
                    <div class="section-3">
                        <div class="content-1">
                            <div class="para-1">
                                <h3>Our Vision</h3>
                                <p> To become the go-to ecosystem for task and project management, driving operational excellence.</p>
                            </div>

                            <div class="para-1">
                                <h3>Our Mission</h3>
                                <p>To become the leading task, project, and best task manager software ecosystem,
                                    a comprehensive Task management software that drives organizational excellence.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="event-section" id="event-id">
                    <div class="event-head">
                        <h1>Manage Tasks, Projects & Teams<br></br>
                            Seamlessly with Best Task and Project
                            Management Software </h1>
                        <p>Empowering Smarter Workflows,  <br></br> Every Day.</p>
                    </div>
                    <div class="event-details">
                        <div class="event-sec1">
                            <div class="event-img-section">
                                <img src="assets/images/event-img1.png" alt="event" class="img-fluid w-100" />
                            </div>
                            {/* <div class="event-img-details">
                                <h1>Time                                    <br></br>
                                    Track</h1>
                                <p>Monitor employee activity daily with Online, Offline, Break & Lunch tracking. View online hours, breaks, and filter by user or date with this task management software.
                                </p>
                                <div class="relative-img">
                                    <img src="assets/images/event-img2.png" alt="event" class="img-fluid w-100" />
                                    <div class="arrow-event">
                                        <img src="assets/images/arrow-event.png" alt="arrow" />
                                    </div>
                                </div>
                            </div> */}
                        </div>
                        <div class="event-sec2">
                            {/* <div class="event-img-section">
                                <img src="assets/images/event-img3.png" alt="event" class="img-fluid w-100" />
                            </div> */}
                            <div class="event-img-details">


                                <div className='timetrack-imgA'>  <h1>TimeTrack</h1>
                                    <p>
                                        Monitor employee activity daily with Online, Offline, Break & Lunch tracking. View online hours, breaks, and filter by user or date with this task management software.


                                    </p></div>
                                <div> <h1>Quick Task

                                    Management</h1>
                                    <p>View and update employee tasks instantly. Add, assign, and manage tasks quickly with minimal effort, ensuring efficient workflow and productivity.

                                    </p></div>
                                <div> <h1>      Project Progress Tracking
                                </h1>
                                    <p>
                                        Track project completion percentages, total tasks, completed tasks, and employee contributions with clear visibility into performance and accountability.

                                    </p></div>
                                <div> <h1>Task Overview Panel</h1>
                                    <p>
                                        Get a complete view of all tasks with start/end dates and allocated time. Track status, manage deadlines, and approve requests with the best task management software.


                                    </p></div>
                                {/* <div class="relative-img">
                                    <img src="assets/images/event-img4.png" alt="event" class="img-fluid w-100" />
                                    <div class="arrow-event">
                                        <img src="assets/images/arrow-event.png" alt="arrow" />
                                    </div>
                                </div> */}
                            </div>

                            <div class="event-img-details">
                                <span class="timetrack-img"><h1>TimeTrack</h1>
                                    <p>
                                        Monitor employee activity daily with Online, Offline, Break & Lunch tracking. View online hours, breaks, and filter by user or date with this task management software.


                                    </p></span>
                                <div class="relative-img">
                                    <img src="assets/images/event-img2.png" alt="event" class="img-fluid w-100" />
                                    <div class="arrow-event">
                                        <img src="assets/images/arrow-event.png" alt="arrow" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    {/* <div class="ring-img-section">
                        <img src="assets/images/event-absolute-img.png" alt="ring" class="img-fluid" />
                    </div> */}
                    <div class="event-absolute-img">
                        <img src="assets/images/event-absolute.png" alt="event line" class="w-100" />
                    </div>
                </div>
                <div class="program-donate-bg">
                    <div class="home-section-4" id="program-id">
                        <div class="section-4">
                            <div class="head-section">
                                <h2>Explore Saihatask Modules & Features</h2>
                                <p>Track tasks, set deadlines, monitor team progress, and organize your entire project pipeline with real-time reporting dashboards.</p>
                                <div class="swiper-button-next"></div>
                                <div class="swiper-button-prev"></div>
                                <div class="swiper-section">
                                    <div class="swiper-container" id="tab-btn">
                                        <div class="swiper-wrapper">
                                            <div class="swiper-slide">
                                                <button class="swiper-btn" data-tab="btn-1">Task Assignment</button>
                                            </div>
                                            <div class="swiper-slide">
                                                <button class="swiper-btn" data-tab="btn-2">Daily Review Logs</button>
                                            </div>
                                            <div class="swiper-slide">
                                                <button class="swiper-btn" data-tab="btn-3">Productivity Scoreboard & Reports</button>
                                            </div>
                                            <div class="swiper-slide">
                                                <button class="swiper-btn" data-tab="btn-4">Push Reminders & Manager Reports</button>
                                            </div>
                                            <div class="swiper-slide">
                                                <button class="swiper-btn" data-tab="btn-5">Team Collaboration</button>
                                            </div>
                                            <div class="swiper-slide">
                                                <button class="swiper-btn" data-tab="btn-6">Task Assignment</button>
                                            </div>
                                            <div class="swiper-slide">
                                                <button class="swiper-btn" data-tab="btn-7">Daily Review Logs</button>
                                            </div>
                                            <div class="swiper-slide">
                                                <button class="swiper-btn" data-tab="btn-8">Productivity Scoreboard & Reports</button>
                                            </div>
                                            <div class="swiper-slide">
                                                <button class="swiper-btn" data-tab="btn-9">Push Reminders & Manager Reports</button>
                                            </div>
                                            <div class="swiper-slide">
                                                <button class="swiper-btn" data-tab="btn-10">Team Collaboration</button>
                                            </div>
                                            <div class="swiper-slide">
                                                <button class="swiper-btn" data-tab="btn-11">Monthly Task Planner</button>
                                            </div>
                                            <div class="swiper-slide">
                                                <button class="swiper-btn" data-tab="btn-12">Employee Report Log
                                                </button>
                                            </div>
                                            <div class="swiper-slide">
                                                <button class="swiper-btn" data-tab="btn-13">Role-Based Access</button>
                                            </div>
                                        </div>

                                    </div>
                                </div>


                            </div>

                            <div class="content-section">
                                <div class="tab-content-1 " id="btn-1">
                                    <div class="tab-card-section">
                                        <div class="sub-card-section1">
                                            <div class="text-end"> <img src="assets/images/slider2.png" alt="logo" class="img-fluid" /></div>
                                            <div>
                                                <h1>
                                                    Assign
                                                    <br></br>
                                                    Tasks with Clarity
                                                </h1>
                                                <p>Create and assign tasks with deadlines, labels, and <br></br>assign owners ensuring everyone knows what to do and <br></br> when to do it.</p>
                                            </div>
                                        </div>
                                        <div class="sub-card-section2">
                                            <div>
                                                <h1>
                                                    Track <br></br>
                                                    Ownership & Progress
                                                </h1>
                                                <p>With powerful best task and project management software, gain fully visibility into who's working on what.
                                                    Track task progress, schedule reminders, and and ensure projects stay on course with real time updates.
                                                </p>
                                            </div>
                                            <div class="position-relative">
                                                <h3>Feature</h3>
                                                <p>Task Assignment & Ownership</p>
                                                <img src="assets/images/slider1.png" alt="logo" class="img-fluid position-absolute" />
                                            </div>

                                        </div>
                                    </div>
                                    <div class="content-1-1">

                                        <div class="circle-div ">
                                            <div class="text-div"> <img src="assets/images/circle-with-text-lg.png" alt="circle-with-text-2" class="img-fluid program21" />
                                                <div class="img-div">
                                                    <img src="assets/images/circle-with-text-lg-logo.png" alt="circle-with-text-lg-logo" class="img-fluid hand-in-circle" />
                                                </div>
                                            </div>

                                        </div>

                                        <div class="date-div">
                                            <div class="para-1">
                                                <div class="img-div"></div>
                                                <p>New Feature Rollout</p>
                                            </div>


                                        </div>

                                        <div class="loc-div">
                                            <div class="para-1">
                                                <div class="img-div"></div>
                                                <p>Available for All Plans</p>

                                            </div>

                                        </div>

                                        <div class="content-1-2">
                                            <div class="h-div">
                                                <h2>Task Assignment</h2>
                                            </div>
                                            <p>
                                                Saihatask simplifies task assignment, tracking, and management making it effortless to lead
                                                distributed teams and handle complex workstreams. As the best task and project management
                                                software, it enables teams to be onboarded in minutes, assigned to roles or departments, and
                                                given precise task ownership.Task execution becomes more transparent, efficient, and
                                                collaborative with real-time updates, calendar synchronization, and mobile-friendly access.
                                                Saihatask reduces bottlenecks, increases accountability, and keeps your entire task flow
                                                connected in one place making it the go-to solution for modern, agile teams.
                                            </p>
                                        </div>

                                        <div class="content-1-3">
                                            <div class="row">
                                                <div class="col-lg-6">
                                                    <div class="img-div123">
                                                        <div class="circle-div circle-div123">
                                                            <div class="text-div"> <img src="assets/images/circle-with-text-sm.png" alt="circle-with-text-sm" class="img-fluid program21" />
                                                                <div class="img-div">
                                                                    <img src="assets/images/circle-with-text-sm-logo.png" alt="logo" class="img-fluid hand-in-circle" />
                                                                </div>
                                                            </div>

                                                        </div>
                                                        <img src="assets/images/tra1.jpg" alt="flood image"
                                                            class="img-fluid" />
                                                    </div>
                                                </div>
                                                <div class="col-lg-6 list-div">
                                                    <h3>Key Benefits</h3>

                                                    <div class="ul-div">
                                                        <ul>
                                                            <li>Assign tasks with deadlines, priorities, and designated owners in just a few clicks
                                                                using the best task and project management software.
                                                            </li>
                                                            <li>Visualize assignments across Boards, Calendar, Timeline, and List views

                                                            </li>
                                                            <li>Receive real-time alerts when tasks are completed, delayed, or reassigned
                                                            </li>
                                                            <li>Enable managers to track ownership and shift responsibilities dynamically
                                                            </li>
                                                            <li>
                                                                Centralized view for cross-departmental planning and reporting
                                                            </li>

                                                        </ul>
                                                    </div>
                                                </div>

                                            </div>


                                        </div>

                                        <div class="content-1-4">
                                            <div class="gallery-section">
                                                <div class="img-div-1">
                                                    <img src="assets/images/urb2.jpg" alt="flood image" />
                                                </div>
                                                <div class="img-div-2">
                                                    <div class="img-div-2-1">
                                                        <img src="assets/images/rul3.jpg" alt="flood image" />
                                                    </div>

                                                    <div class="img-div-2-2">
                                                        <img src="assets/images/rur4.jpg" alt="flood image" />
                                                    </div>
                                                </div>

                                                <div class="img-div-3">
                                                    <img src="assets/images/rur5.jpg" alt="flood image" />
                                                </div>
                                                <div class="img-div-4">
                                                    <img src="assets/images/rur6.jpg" alt="flood image" />
                                                </div>
                                            </div>

                                        </div>


                                    </div>
                                </div>
                                <div class="tab-content-1 " id="btn-2">
                                    <div class="tab-card-section">
                                        <div class="sub-card-section1">
                                            <div class="text-end"> <img src="assets/images/slider2.png" alt="logo" class="img-fluid" /></div>
                                            <div>
                                                <h1>
                                                    On Track
                                                    <br></br>
                                                    with Daily Reviews
                                                </h1>
                                                <p>Promote accountability with structured daily   review inputs tailored for each user. Easily monitor completed,  pending, or delayed tasks.</p>
                                            </div>
                                        </div>
                                        <div class="sub-card-section2">
                                            <div>
                                                <h1>
                                                    Improve  <br></br>
                                                    Communication Flow
                                                </h1>
                                                <p>Managers get instant visibility into team progress,   blockers, and daily highlights with the best tasks and project management software-ensuring nothing slips through the cracks.</p>
                                            </div>
                                            <div class="position-relative">
                                                <h3>WIth Daily Review Logs</h3>
                                                <p>Turn daily check-ins into daily wins.</p>
                                                <img src="assets/images/slider1.png" alt="logo" class="img-fluid position-absolute" />
                                            </div>

                                        </div>
                                    </div>
                                    <div class="content-1-1">
                                        <div class="circle-div ">
                                            <div class="text-div"> <img src="assets/images/circle-with-text-lg.png" alt="circle-with-text" class="img-fluid program21" />
                                                <div class="img-div">
                                                    <img src="assets/images/circle-with-text-lg-logo.png" alt="logo" class="img-fluid hand-in-circle" />
                                                </div>
                                            </div>

                                        </div>
                                        <div class="date-div">
                                            <div class="para-1">
                                                <div class="img-div"></div>
                                                <p>New Feature Rollout</p>
                                            </div>


                                        </div>

                                        <div class="loc-div">
                                            <div class="para-1">
                                                <div class="img-div"></div>
                                                <p>Available for All Plans</p>

                                            </div>

                                        </div>

                                        <div class="content-1-2">
                                            <div class="h-div">
                                                <h2>Daily Review Logs</h2>
                                            </div>
                                            <p>In fast-paced teams, staying aligned daily is non-negotiable. Saihatask’s Daily Review Logs provide a structured framework for team members to share daily updates, enabling managers and leads to gain instant visibility into progress, blockers, and potential delays.
                                                As one of the best task and project management software options available, it fosters a culture of accountability, transparency, and continuous improvement without the need for long meetings.
                                            </p>
                                            <p>
                                                Whether you're remote, hybrid, or on-site, daily review inputs keep everyone focused and synchronized. Saihatask turns these insights into actionable summaries, helping you lead smarter every day.
                                            </p>
                                        </div>

                                        <div class="content-1-3">
                                            <div class="row">
                                                <div class="col-lg-6">
                                                    <div class="img-div123">
                                                        <div class="circle-div circle-div123">
                                                            <div class="text-div"> <img src="assets/images/circle-with-text-sm.png" alt="circle-with-text" class="img-fluid program21" />
                                                                <div class="img-div">
                                                                    <img src="assets/images/circle-with-text-sm-logo.png" alt="logo" class="img-fluid hand-in-circle" />
                                                                </div>
                                                            </div>

                                                        </div>
                                                        <img src="assets/images/tra1.jpg" alt="flood image"
                                                            class="img-fluid" />
                                                    </div>
                                                </div>
                                                <div class="col-lg-6 list-div">
                                                    <h3>Key Benefits</h3>

                                                    <div class="ul-div">
                                                        <ul>
                                                            <li>Capture daily task status updates from every user effortlessly


                                                            </li>
                                                            <li>Easily identify delays, blockers, and completed tasks at a glance—all in a single view

                                                            </li>
                                                            <li>Empower managers to give timely feedback and resolve issues before they escalate
                                                            </li>
                                                            <li>
                                                                Automate reminders for users to submit daily logs

                                                            </li>
                                                            <li>Minimize the need for status meetings while promoting accountability and self-discipline within the team</li>

                                                        </ul>
                                                    </div>
                                                </div>

                                            </div>


                                        </div>

                                        <div class="content-1-4">
                                            <div class="gallery-section">
                                                <div class="img-div-1">
                                                    <img src="assets/images/urb2.jpg" alt="flood image" />
                                                </div>
                                                <div class="img-div-2">
                                                    <div class="img-div-2-1">
                                                        <img src="assets/images/rul3.jpg" alt="flood image" />
                                                    </div>

                                                    <div class="img-div-2-2">
                                                        <img src="assets/images/rur4.jpg" alt="flood image" />
                                                    </div>
                                                </div>

                                                <div class="img-div-3">
                                                    <img src="assets/images/rur5.jpg" alt="flood image" />
                                                </div>
                                                <div class="img-div-4">
                                                    <img src="assets/images/rur6.jpg" alt="flood image" />
                                                </div>
                                            </div>

                                        </div>


                                    </div>
                                </div>
                                <div class="tab-content-1 " id="btn-3">
                                    <div class="tab-card-section">
                                        <div class="sub-card-section1">
                                            <div class="text-end"> <img src="assets/images/slider2.png" alt="logo" class="img-fluid" /></div>
                                            <div>
                                                <h1>
                                                    Measure
                                                    <br></br>
                                                    What Matters
                                                </h1>
                                                <p>Track team and individual productivity through <br></br> real-time dashboards, activity logs, <br></br> and performance metrics with powerful Task  management software <br></br>
                                                    designed to keep your team aligned and accountable.
                                                </p>
                                            </div>
                                        </div>
                                        <div class="sub-card-section2">
                                            <div>
                                                <h1>
                                                    Smarter Reports,<br></br>
                                                    Better Decisions
                                                </h1>
                                                <p>Receive automated monthly reports, task completion <br></br> trends, and data-driven insights <br></br> to boost workflow efficiency.</p>
                                            </div>
                                            <div class="position-relative">
                                                <h3>Productivity Scoreboard</h3>
                                                <p>What’s measured, gets managed.</p>
                                                <img src="assets/images/slider1.png" alt="logo" class="img-fluid position-absolute" />
                                            </div>

                                        </div>
                                    </div>
                                    <div class="content-1-1">
                                        <div class="circle-div ">
                                            <div class="text-div"> <img src="assets/images/circle-with-text-lg.png" alt="circle-with-text" class="img-fluid program21" />
                                                <div class="img-div">
                                                    <img src="assets/images/circle-with-text-lg-logo.png" alt="logo" class="img-fluid hand-in-circle" />
                                                </div>
                                            </div>

                                        </div>
                                        <div class="date-div">
                                            <div class="para-1">
                                                <div class="img-div"></div>
                                                <p>New Feature Rollout</p>
                                            </div>


                                        </div>

                                        <div class="loc-div">
                                            <div class="para-1">
                                                <div class="img-div"></div>
                                                <p>Available for All Plans</p>

                                            </div>

                                        </div>

                                        <div class="content-1-2">
                                            <div class="h-div">
                                                <h2>Productivity Scoreboard & Reports</h2>
                                            </div>
                                            <p>Understanding how your team performs shouldn’t require guesswork. Saihatask’s Productivity Scoreboard gives you real-time insights into task completion, user activity, and overall team performance. As a robust Task management software, it provides visual charts, heatmaps, and summary reports help you identify top performers, address inefficiencies, and make data-backed decisions.
                                            </p>
                                            <p>
                                                Saihatask transforms daily task data into meaningful monthly performance insights—empowering you to lead with clarity and drive confident growth.
                                            </p>
                                        </div>

                                        <div class="content-1-3">
                                            <div class="row">
                                                <div class="col-lg-6">
                                                    <div class="img-div123">
                                                        <div class="circle-div circle-div123">
                                                            <div class="text-div"> <img src="assets/images/circle-with-text-sm.png" alt="circle-with-text" class="img-fluid program21" />
                                                                <div class="img-div">
                                                                    <img src="assets/images/circle-with-text-sm-logo.png" alt="logo" class="img-fluid hand-in-circle" />
                                                                </div>
                                                            </div>

                                                        </div>
                                                        <img src="assets/images/tra1.jpg" alt="flood image"
                                                            class="img-fluid" />
                                                    </div>
                                                </div>
                                                <div class="col-lg-6 list-div">
                                                    <h3>Key Benefits</h3>

                                                    <div class="ul-div">
                                                        <ul>
                                                            <li>Real-time dashboards that reflect user and team productivity


                                                            </li>
                                                            <li>Auto-generated weekly/monthly reports for managers and admins

                                                            </li>
                                                            <li>Performance charts to track completion rates, delays, and trends
                                                            </li>
                                                            <li>
                                                                Identify high performers and improvement areas instantly

                                                            </li>
                                                            <li>Downloadable reports to support reviews and strategic decisions</li>

                                                        </ul>
                                                    </div>
                                                </div>

                                            </div>


                                        </div>

                                        <div class="content-1-4">
                                            <div class="gallery-section">
                                                <div class="img-div-1">
                                                    <img src="assets/images/urb2.jpg" alt="flood image" />
                                                </div>
                                                <div class="img-div-2">
                                                    <div class="img-div-2-1">
                                                        <img src="assets/images/rul3.jpg" alt="flood image" />
                                                    </div>

                                                    <div class="img-div-2-2">
                                                        <img src="assets/images/rur4.jpg" alt="flood image" />
                                                    </div>
                                                </div>

                                                <div class="img-div-3">
                                                    <img src="assets/images/rur5.jpg" alt="flood image" />
                                                </div>
                                                <div class="img-div-4">
                                                    <img src="assets/images/rur6.jpg" alt="flood image" />
                                                </div>
                                            </div>

                                        </div>

                                    </div>
                                </div>
                                <div class="tab-content-1 " id="btn-4">
                                    <div class="tab-card-section">
                                        <div class="sub-card-section1">
                                            <div class="text-end"> <img src="assets/images/slider2.png" alt="logo" class="img-fluid" /></div>
                                            <div>
                                                <h1>
                                                    Never
                                                    <br></br>
                                                    Miss a Beat
                                                </h1>
                                                <p>Automated push notifications keep users informed <br></br>about task updates, deadlines, comments, and  <br></br> follow-ups—right on time.</p>
                                            </div>
                                        </div>
                                        <div class="sub-card-section2">
                                            <div>
                                                <h1>
                                                    Managers  <br></br>
                                                    Stay in the Loop
                                                </h1>
                                                <p>Instant summary reports and alerts are sent to managers through powerful Task management software for missed reviews, delayed tasks, and inactivity ensuring accountability at every level.</p>
                                            </div>
                                            <div class="position-relative">
                                                <h3>Smart Alerts & Reports

                                                </h3>
                                                <p>Timely Nudges Drive Timely Actions.</p>
                                                <img src="assets/images/slider1.png" alt="logo" class="img-fluid position-absolute" />
                                            </div>

                                        </div>
                                    </div>
                                    <div class="content-1-1">
                                        <div class="circle-div ">
                                            <div class="text-div"> <img src="assets/images/circle-with-text-lg.png" alt="circle-with-text" class="img-fluid program21" />
                                                <div class="img-div">
                                                    <img src="assets/images/circle-with-text-lg-logo.png" alt="logo" class="img-fluid hand-in-circle" />
                                                </div>
                                            </div>

                                        </div>
                                        <div class="date-div">
                                            <div class="para-1">
                                                <div class="img-div"></div>
                                                <p>New Feature Rollout</p>
                                            </div>


                                        </div>

                                        <div class="loc-div">
                                            <div class="para-1">
                                                <div class="img-div"></div>
                                                <p>Available for All Plans</p>

                                            </div>

                                        </div>
                                        <div class="content-1-2">
                                            <div class="h-div">
                                                <h2>Push Reminders & Manager Reports</h2>
                                            </div>
                                            <p>In a busy work environment, it’s easy for things to slip through the cracks. Saihatask’s Push
                                                Reminders ensure that no task is forgotten and every action is followed up—on time. As part of
                                                the best task and project management software, users get automated reminders for upcoming
                                                deadlines, missed reviews, and pending updates, keeping them focused and on track.
                                            </p>
                                            <p>Manager Reports provide clear, high-level insights with real-time alerts on delays, inactivity, and  performance—enabling leaders to act promptly without the need for micromanagement.</p>
                                        </div>

                                        <div class="content-1-3">
                                            <div class="row">
                                                <div class="col-lg-6">
                                                    <div class="img-div123">
                                                        <div class="circle-div circle-div123">
                                                            <div class="text-div"> <img src="assets/images/circle-with-text-sm.png" alt="circle-with-text" class="img-fluid program21" />
                                                                <div class="img-div">
                                                                    <img src="assets/images/circle-with-text-sm-logo.png" alt="logo" class="img-fluid hand-in-circle" />
                                                                </div>
                                                            </div>

                                                        </div>
                                                        <img src="assets/images/tra1.jpg" alt="flood image"
                                                            class="img-fluid" />
                                                    </div>
                                                </div>
                                                <div class="col-lg-6 list-div">
                                                    <h3>Key Benefits</h3>

                                                    <div class="ul-div">
                                                        <ul>
                                                            <li>Automatic push reminders for tasks, deadlines, and daily reviews


                                                            </li>
                                                            <li>Instant Whatsapp & in-app alerts for delays, feedback, and status updates

                                                            </li>
                                                            <li>Manager-level summary reports on user performance and engagement
                                                            </li>
                                                            <li>Reduce manual follow-ups with proactive notification systems powered by smart Task management software
                                                            </li>
                                                            <li>Better time management and accountability across the organization</li>

                                                        </ul>
                                                    </div>
                                                </div>

                                            </div>


                                        </div>

                                        <div class="content-1-4">
                                            <div class="gallery-section">
                                                <div class="img-div-1">
                                                    <img src="assets/images/urb2.jpg" alt="flood image" />
                                                </div>
                                                <div class="img-div-2">
                                                    <div class="img-div-2-1">
                                                        <img src="assets/images/rul3.jpg" alt="flood image" />
                                                    </div>

                                                    <div class="img-div-2-2">
                                                        <img src="assets/images/rur4.jpg" alt="flood image" />
                                                    </div>
                                                </div>

                                                <div class="img-div-3">
                                                    <img src="assets/images/rur5.jpg" alt="flood image" />
                                                </div>
                                                <div class="img-div-4">
                                                    <img src="assets/images/rur6.jpg" alt="flood image" />
                                                </div>
                                            </div>

                                        </div>


                                    </div>
                                </div>
                                <div class="tab-content-1 " id="btn-5">
                                    <div class="tab-card-section">
                                        <div class="sub-card-section1">
                                            <div class="text-end"> <img src="assets/images/slider2.png" alt="logo" class="img-fluid" /></div>
                                            <div>
                                                <h1>
                                                    Work
                                                    <br></br>
                                                    Together, Seamlessly
                                                </h1>
                                                <p>Assign tasks, share updates, and communicate<br></br> through comments and mentions all within <br></br> a centralized workspace.</p>
                                            </div>
                                        </div>
                                        <div class="sub-card-section2">
                                            <div>
                                                <h1>
                                                    No Silos, <br></br>
                                                    Just Sync
                                                </h1>
                                                <p>Keep your team aligned with real-time updates,  file sharing, and collaborative task boards that ensure everyone’s on the same page-powered by the best task and project management
                                                    software to streamline teamwork and communication.</p>
                                            </div>
                                            <div class="position-relative">
                                                <h3>Saihatask Collaboration

                                                </h3>
                                                <p>Great teamwork starts with smart tools.</p>
                                                <img src="assets/images/slider1.png" alt="logo" class="img-fluid position-absolute" />
                                            </div>

                                        </div>
                                    </div>
                                    <div class="content-1-1">
                                        <div class="circle-div ">
                                            <div class="text-div"> <img src="assets/images/circle-with-text-lg.png" alt="circle-with-text" class="img-fluid program21" />
                                                <div class="img-div">
                                                    <img src="assets/images/circle-with-text-lg-logo.png" alt="logo" class="img-fluid hand-in-circle" />
                                                </div>
                                            </div>

                                        </div>
                                        <div class="date-div">
                                            <div class="para-1">
                                                <div class="img-div"></div>
                                                <p>New Feature Rollout</p>
                                            </div>


                                        </div>

                                        <div class="loc-div">
                                            <div class="para-1">
                                                <div class="img-div"></div>
                                                <p>Available for All Plans</p>

                                            </div>

                                        </div>

                                        <div class="content-1-2">
                                            <div class="h-div">
                                                <h2>Team Collaboration</h2>
                                            </div>
                                            <p>Collaboration shouldn't feel like chaos. Saihatask, the best task and project management
                                                software, brings your team together in one shared workspace where chats, files, and task
                                                updates live side by side. Collaborate directly within tasks, share real-time updates, and keep
                                                everyone aligned—no need to switch between tools.
                                            </p>
                                            <p>Whether your team is remote, hybrid, or in-office, Saihatask ensures seamless communication, faster decisions, and aligned execution across every project.</p>
                                        </div>

                                        <div class="content-1-3">
                                            <div class="row">
                                                <div class="col-lg-6">
                                                    <div class="img-div123">
                                                        <div class="circle-div circle-div123">
                                                            <div class="text-div"> <img src="assets/images/circle-with-text-sm.png" alt="circle-with-text" class="img-fluid program21" />
                                                                <div class="img-div">
                                                                    <img src="assets/images/circle-with-text-sm-logo.png" alt="logo" class="img-fluid hand-in-circle" />
                                                                </div>
                                                            </div>

                                                        </div>
                                                        <img src="assets/images/tra1.jpg" alt="flood image"
                                                            class="img-fluid" />
                                                    </div>
                                                </div>
                                                <div class="col-lg-6 list-div">
                                                    <h3>Key Benefits</h3>

                                                    <div class="ul-div">
                                                        <ul>
                                                            <li>Chat within the platform to keep conversations focused and contextual


                                                            </li>
                                                            <li>Centralized workspace for discussions, file sharing, and task updates

                                                            </li>
                                                            <li>Collaborate seamlessly across departments using shared boards and dashboards powered by smart Team collaboration software
                                                            </li>
                                                            <li>Enable smooth handovers and accelerate decision-making
                                                            </li>
                                                            <li>Promote a culture built on collaboration, clear communication, and full transparency.
                                                            </li>

                                                        </ul>
                                                    </div>
                                                </div>

                                            </div>


                                        </div>

                                        <div class="content-1-4">
                                            <div class="gallery-section">
                                                <div class="img-div-1">
                                                    <img src="assets/images/urb2.jpg" alt="flood image" />
                                                </div>
                                                <div class="img-div-2">
                                                    <div class="img-div-2-1">
                                                        <img src="assets/images/rul3.jpg" alt="flood image" />
                                                    </div>

                                                    <div class="img-div-2-2">
                                                        <img src="assets/images/rur4.jpg" alt="flood image" />
                                                    </div>
                                                </div>

                                                <div class="img-div-3">
                                                    <img src="assets/images/rur5.jpg" alt="flood image" />
                                                </div>
                                                <div class="img-div-4">
                                                    <img src="assets/images/rur6.jpg" alt="flood image" />
                                                </div>
                                            </div>

                                        </div>


                                    </div>
                                </div>
                                <div class="tab-content-1 " id="btn-6">
                                    <div class="tab-card-section">
                                        <div class="sub-card-section1">
                                            <div class="text-end"> <img src="assets/images/slider2.png" alt="logo" class="img-fluid" /></div>
                                            <div>
                                                <h1>
                                                    Assign
                                                    <br></br>
                                                    Tasks with Clarity
                                                </h1>
                                                <p>Create and assign tasks with deadlines, labels, and <br></br> owners ensuring everyone knows what to do and <br></br> when to do it.</p>
                                            </div>
                                        </div>
                                        <div class="sub-card-section2">
                                            <div>
                                                <h1>
                                                    Track <br></br>
                                                    Ownership & Progress
                                                </h1>
                                                <p>Gain full visibility into who’s working on what. Monitor <br></br>  task status, set reminders, and keep projects on <br></br> track with real-time updates.</p>
                                            </div>
                                            <div class="position-relative">
                                                <h3>Feature</h3>
                                                <p>Task Assignment & Ownership</p>
                                                <img src="assets/images/slider1.png" alt="logo" class="img-fluid position-absolute" />
                                            </div>

                                        </div>
                                    </div>
                                    <div class="content-1-1">

                                        <div class="circle-div ">
                                            <div class="text-div"> <img src="assets/images/circle-with-text-lg.png" alt="circle-with-text" class="img-fluid program21" />
                                                <div class="img-div">
                                                    <img src="assets/images/circle-with-text-lg-logo.png" alt="logo" class="img-fluid hand-in-circle" />
                                                </div>
                                            </div>

                                        </div>

                                        <div class="date-div">
                                            <div class="para-1">
                                                <div class="img-div"></div>
                                                <p>New Feature Rollout</p>
                                            </div>


                                        </div>

                                        <div class="loc-div">
                                            <div class="para-1">
                                                <div class="img-div"></div>
                                                <p>Available for All Plans</p>

                                            </div>

                                        </div>

                                        <div class="content-1-2">
                                            <div class="h-div">
                                                <h2>Task Assignment</h2>
                                            </div>
                                            <p>When managing distributed teams and complex workstreams, Saihatask empowers organizations to assign, track, and manage tasks effortlessly. Teams are onboarded in minutes, assigned to roles or departments, and given precise task ownership. With live updates, calendar sync, and mobile-friendly access, task execution becomes transparent, timely, and collaborative. Saihatask reduces bottlenecks, increases accountability, and keeps your entire task flow connected in one place.</p>
                                        </div>

                                        <div class="content-1-3">
                                            <div class="row">
                                                <div class="col-lg-6">
                                                    <div class="img-div123">
                                                        <div class="circle-div circle-div123">
                                                            <div class="text-div"> <img src="assets/images/circle-with-text-sm.png" alt="circle-with-text" class="img-fluid program21" />
                                                                <div class="img-div">
                                                                    <img src="assets/images/circle-with-text-sm-logo.png" alt="logo" class="img-fluid hand-in-circle" />
                                                                </div>
                                                            </div>

                                                        </div>
                                                        <img src="assets/images/tra1.jpg" alt="flood image"
                                                            class="img-fluid" />
                                                    </div>
                                                </div>
                                                <div class="col-lg-6 list-div">
                                                    <h3>Key Benefits</h3>

                                                    <div class="ul-div">
                                                        <ul>
                                                            <li>Assign tasks with deadlines, priorities, and designated owners in just a few clicks


                                                            </li>
                                                            <li>Visualize assignments across Boards, Calendar, Timeline, and List views

                                                            </li>
                                                            <li>Receive real-time alerts when tasks are completed, delayed, or reassigned
                                                            </li>
                                                            <li>Enable managers to track ownership and shift responsibilities dynamically
                                                            </li>
                                                            <li>
                                                                Centralized view for cross-departmental planning and reporting
                                                            </li>

                                                        </ul>
                                                    </div>
                                                </div>

                                            </div>


                                        </div>

                                        <div class="content-1-4">
                                            <div class="gallery-section">
                                                <div class="img-div-1">
                                                    <img src="assets/images/urb2.jpg" alt="flood image" />
                                                </div>
                                                <div class="img-div-2">
                                                    <div class="img-div-2-1">
                                                        <img src="assets/images/rul3.jpg" alt="flood image" />
                                                    </div>

                                                    <div class="img-div-2-2">
                                                        <img src="assets/images/rur4.jpg" alt="flood image" />
                                                    </div>
                                                </div>

                                                <div class="img-div-3">
                                                    <img src="assets/images/rur5.jpg" alt="flood image" />
                                                </div>
                                                <div class="img-div-4">
                                                    <img src="assets/images/rur6.jpg" alt="flood image" />
                                                </div>
                                            </div>

                                        </div>


                                    </div>
                                </div>
                                <div class="tab-content-1 " id="btn-7">
                                    <div class="tab-card-section">
                                        <div class="sub-card-section1">
                                            <div class="text-end"> <img src="assets/images/slider2.png" alt="logo" class="img-fluid" /></div>
                                            <div>
                                                <h1>
                                                    On Track
                                                    <br></br>
                                                    with Daily Reviews
                                                </h1>
                                                <p>Encourage accountability with structured daily  <br></br> review inputs for every user. Easily monitor completed,  <br></br> pending, or delayed tasks.</p>
                                            </div>
                                        </div>
                                        <div class="sub-card-section2">
                                            <div>
                                                <h1>
                                                    Improve  <br></br>
                                                    Communication Flow
                                                </h1>
                                                <p>Managers get instant visibility into team progress,  <br></br>  blockers, and daily highlights—ensuring nothing  <br></br> slips through the cracks.</p>
                                            </div>
                                            <div class="position-relative">
                                                <h3>WIth Daily Review Logs</h3>
                                                <p>Turn daily check-ins into daily wins.</p>
                                                <img src="assets/images/slider1.png" alt="logo" class="img-fluid position-absolute" />
                                            </div>

                                        </div>
                                    </div>
                                    <div class="content-1-1">
                                        <div class="circle-div ">
                                            <div class="text-div"> <img src="assets/images/circle-with-text-lg.png" alt="circle-with-text" class="img-fluid program21" />
                                                <div class="img-div">
                                                    <img src="assets/images/circle-with-text-lg-logo.png" alt="logo" class="img-fluid hand-in-circle" />
                                                </div>
                                            </div>

                                        </div>
                                        <div class="date-div">
                                            <div class="para-1">
                                                <div class="img-div"></div>
                                                <p>New Feature Rollout</p>
                                            </div>


                                        </div>

                                        <div class="loc-div">
                                            <div class="para-1">
                                                <div class="img-div"></div>
                                                <p>Available for All Plans</p>

                                            </div>

                                        </div>

                                        <div class="content-1-2">
                                            <div class="h-div">
                                                <h2>Daily Review Logs</h2>
                                            </div>
                                            <p>In fast-paced teams, staying aligned daily is non-negotiable. Saihatask’s Daily Review Logs offer a structured way for every team member to submit daily updates, helping managers and leads get instant clarity on progress, blockers, and delays. This fosters a culture of accountability, transparency, and continuous improvement—without the need for long meetings.

                                            </p>
                                            <p>
                                                Whether you're remote, hybrid, or on-site, daily review inputs keep everyone focused and synchronized. Saihatask turns these insights into actionable summaries, helping you lead smarter every day.
                                            </p>
                                        </div>

                                        <div class="content-1-3">
                                            <div class="row">
                                                <div class="col-lg-6">
                                                    <div class="img-div123">
                                                        <div class="circle-div circle-div123">
                                                            <div class="text-div"> <img src="assets/images/circle-with-text-sm.png" alt="" class="img-fluid program21" />
                                                                <div class="img-div">
                                                                    <img src="assets/images/circle-with-text-sm-logo.png" alt="logo" class="img-fluid hand-in-circle" />
                                                                </div>
                                                            </div>

                                                        </div>
                                                        <img src="assets/images/tra1.jpg" alt="flood image"
                                                            class="img-fluid" />
                                                    </div>
                                                </div>
                                                <div class="col-lg-6 list-div">
                                                    <h3>Key Benefits</h3>

                                                    <div class="ul-div">
                                                        <ul>
                                                            <li>Capture daily task status updates from every user effortlessly


                                                            </li>
                                                            <li>Instantly spot delays, blockers, and completed work in one view

                                                            </li>
                                                            <li>Enable managers to provide feedback and address issues proactively
                                                            </li>
                                                            <li>
                                                                Automate reminders for users to submit daily logs

                                                            </li>
                                                            <li>Reduce status meetings and drive self-discipline across the team</li>

                                                        </ul>
                                                    </div>
                                                </div>

                                            </div>


                                        </div>

                                        <div class="content-1-4">
                                            <div class="gallery-section">
                                                <div class="img-div-1">
                                                    <img src="assets/images/urb2.jpg" alt="flood image" />
                                                </div>
                                                <div class="img-div-2">
                                                    <div class="img-div-2-1">
                                                        <img src="assets/images/rul3.jpg" alt="flood image" />
                                                    </div>

                                                    <div class="img-div-2-2">
                                                        <img src="assets/images/rur4.jpg" alt="flood image" />
                                                    </div>
                                                </div>

                                                <div class="img-div-3">
                                                    <img src="assets/images/rur5.jpg" alt="flood image" />
                                                </div>
                                                <div class="img-div-4">
                                                    <img src="assets/images/rur6.jpg" alt="flood image" />
                                                </div>
                                            </div>

                                        </div>


                                    </div>
                                </div>
                                <div class="tab-content-1 " id="btn-8">
                                    <div class="tab-card-section">
                                        <div class="sub-card-section1">
                                            <div class="text-end"> <img src="assets/images/slider2.png" alt="logo" class="img-fluid" /></div>
                                            <div>
                                                <h1>
                                                    Measure
                                                    <br></br>
                                                    What Matters
                                                </h1>
                                                <p>Track team and individual productivity through <br></br> real-time dashboards, activity logs, <br></br> and performance metrics.</p>
                                            </div>
                                        </div>
                                        <div class="sub-card-section2">
                                            <div>
                                                <h1>
                                                    Smarter Reports,<br></br>
                                                    Better Decisions
                                                </h1>
                                                <p>Get auto-generated monthly reports, task completion<br></br> trends, and actionable insights <br></br> to improve workflow efficiency.</p>
                                            </div>
                                            <div class="position-relative">
                                                <h3>With Productivity Scoreboard</h3>
                                                <p>Because what gets measured, gets managed.</p>
                                                <img src="assets/images/slider1.png" alt="logo" class="img-fluid position-absolute" />
                                            </div>

                                        </div>
                                    </div>
                                    <div class="content-1-1">
                                        <div class="circle-div ">
                                            <div class="text-div"> <img src="assets/images/circle-with-text-lg.png" alt="circle-with-text" class="img-fluid program21" />
                                                <div class="img-div">
                                                    <img src="assets/images/circle-with-text-lg-logo.png" alt="logo" class="img-fluid hand-in-circle" />
                                                </div>
                                            </div>

                                        </div>
                                        <div class="date-div">
                                            <div class="para-1">
                                                <div class="img-div"></div>
                                                <p>New Feature Rollout</p>
                                            </div>


                                        </div>

                                        <div class="loc-div">
                                            <div class="para-1">
                                                <div class="img-div"></div>
                                                <p>Available for All Plans</p>

                                            </div>

                                        </div>

                                        <div class="content-1-2">
                                            <div class="h-div">
                                                <h2>Productivity Scoreboard & Reports</h2>
                                            </div>
                                            <p>Understanding how your team performs shouldn’t require guesswork. Saihatask’s Productivity Scoreboard gives you real-time insights into task completion, user activity, and overall team performance. Visual charts, heatmaps, and summary reports help you identify top performers, address inefficiencies, and make data-backed decisions.

                                            </p>
                                            <p>
                                                From daily task stats to monthly performance reviews, Saihatask turns raw data into actionable insights—so you can lead with clarity and grow with confidence.
                                            </p>
                                        </div>

                                        <div class="content-1-3">
                                            <div class="row">
                                                <div class="col-lg-6">
                                                    <div class="img-div123">
                                                        <div class="circle-div circle-div123">
                                                            <div class="text-div"> <img src="assets/images/circle-with-text-sm.png" alt="circle-with-text" class="img-fluid program21" />
                                                                <div class="img-div">
                                                                    <img src="assets/images/circle-with-text-sm-logo.png" alt="logo" class="img-fluid hand-in-circle" />
                                                                </div>
                                                            </div>

                                                        </div>
                                                        <img src="assets/images/tra1.jpg" alt="flood image"
                                                            class="img-fluid" />
                                                    </div>
                                                </div>
                                                <div class="col-lg-6 list-div">
                                                    <h3>Key Benefits</h3>

                                                    <div class="ul-div">
                                                        <ul>
                                                            <li>Real-time dashboards that reflect user and team productivity


                                                            </li>
                                                            <li>Auto-generated weekly/monthly reports for managers and admins

                                                            </li>
                                                            <li>Performance charts to track completion rates, delays, and trends
                                                            </li>
                                                            <li>
                                                                Identify high performers and improvement areas instantly

                                                            </li>
                                                            <li>Exportable reports to support reviews and strategic decisions</li>

                                                        </ul>
                                                    </div>
                                                </div>

                                            </div>


                                        </div>

                                        <div class="content-1-4">
                                            <div class="gallery-section">
                                                <div class="img-div-1">
                                                    <img src="assets/images/urb2.jpg" alt="flood image" />
                                                </div>
                                                <div class="img-div-2">
                                                    <div class="img-div-2-1">
                                                        <img src="assets/images/rul3.jpg" alt="flood image" />
                                                    </div>

                                                    <div class="img-div-2-2">
                                                        <img src="assets/images/rur4.jpg" alt="flood image" />
                                                    </div>
                                                </div>

                                                <div class="img-div-3">
                                                    <img src="assets/images/rur5.jpg" alt="flood image" />
                                                </div>
                                                <div class="img-div-4">
                                                    <img src="assets/images/rur6.jpg" alt="flood image" />
                                                </div>
                                            </div>

                                        </div>

                                    </div>
                                </div>
                                <div class="tab-content-1 " id="btn-9">
                                    <div class="tab-card-section">
                                        <div class="sub-card-section1">
                                            <div class="text-end"> <img src="assets/images/slider2.png" alt="logo" class="img-fluid" /></div>
                                            <div>
                                                <h1>
                                                    Never
                                                    <br></br>
                                                    Miss a Beat
                                                </h1>
                                                <p>Automated push notifications keep users informed <br></br>about task updates, deadlines, comments, and  <br></br> follow-ups right on time.</p>
                                            </div>
                                        </div>
                                        <div class="sub-card-section2">
                                            <div>
                                                <h1>
                                                    Managers  <br></br>
                                                    Stay in the Loop
                                                </h1>
                                                <p>Instant summary reports and alerts are sent to managers <br></br>  for missed reviews, delayed tasks, and inactivity—ensuring<br></br>  accountability at every level.</p>
                                            </div>
                                            <div class="position-relative">
                                                <h3>With Smart Alerts & Reports

                                                </h3>
                                                <p>Because timely nudges create timely actions.</p>
                                                <img src="assets/images/slider1.png" alt="logo" class="img-fluid position-absolute" />
                                            </div>

                                        </div>
                                    </div>
                                    <div class="content-1-1">
                                        <div class="circle-div ">
                                            <div class="text-div"> <img src="assets/images/circle-with-text-lg.png" alt="circle-with-text" class="img-fluid program21" />
                                                <div class="img-div">
                                                    <img src="assets/images/circle-with-text-lg-logo.png" alt="logo" class="img-fluid hand-in-circle" />
                                                </div>
                                            </div>

                                        </div>
                                        <div class="date-div">
                                            <div class="para-1">
                                                <div class="img-div"></div>
                                                <p>New Feature Rollout</p>
                                            </div>


                                        </div>

                                        <div class="loc-div">
                                            <div class="para-1">
                                                <div class="img-div"></div>
                                                <p>Available for All Plans</p>

                                            </div>

                                        </div>
                                        <div class="content-1-2">
                                            <div class="h-div">
                                                <h2>Push Reminders & Manager Reports</h2>
                                            </div>
                                            <p>In a busy work environment, it’s easy for things to slip through the cracks. Saihatask’s Push Reminders ensure that no task is forgotten and every action is followed up on time. Users get automated reminders for upcoming deadlines, missed reviews, and pending updates, keeping them focused and on track.

                                            </p>
                                            <p>At the same time, Manager Reports deliver high-level visibility with alerts on delays, inactivity, and overall team health empowering leaders to take timely action without micromanagement.</p>
                                        </div>

                                        <div class="content-1-3">
                                            <div class="row">
                                                <div class="col-lg-6">
                                                    <div class="img-div123">
                                                        <div class="circle-div circle-div123">
                                                            <div class="text-div"> <img src="assets/images/circle-with-text-sm.png" alt="circle-with-text" class="img-fluid program21" />
                                                                <div class="img-div">
                                                                    <img src="assets/images/circle-with-text-sm-logo.png" alt="logo" class="img-fluid hand-in-circle" />
                                                                </div>
                                                            </div>

                                                        </div>
                                                        <img src="assets/images/tra1.jpg" alt="flood image"
                                                            class="img-fluid" />
                                                    </div>
                                                </div>
                                                <div class="col-lg-6 list-div">
                                                    <h3>Key Benefits</h3>

                                                    <div class="ul-div">
                                                        <ul>
                                                            <li>Automatic push reminders for tasks, deadlines, and daily reviews


                                                            </li>
                                                            <li>Whatsapp & in-app alerts for delays, feedback, and status changes

                                                            </li>
                                                            <li>Manager-level summary reports on user performance and engagement
                                                            </li>
                                                            <li>Reduce manual follow-ups with proactive notification systems
                                                            </li>
                                                            <li>Better time management and accountability across the organization</li>

                                                        </ul>
                                                    </div>
                                                </div>

                                            </div>


                                        </div>

                                        <div class="content-1-4">
                                            <div class="gallery-section">
                                                <div class="img-div-1">
                                                    <img src="assets/images/urb2.jpg" alt="flood image" />
                                                </div>
                                                <div class="img-div-2">
                                                    <div class="img-div-2-1">
                                                        <img src="assets/images/rul3.jpg" alt="flood image" />
                                                    </div>

                                                    <div class="img-div-2-2">
                                                        <img src="assets/images/rur4.jpg" alt="flood image" />
                                                    </div>
                                                </div>

                                                <div class="img-div-3">
                                                    <img src="assets/images/rur5.jpg" alt="flood image" />
                                                </div>
                                                <div class="img-div-4">
                                                    <img src="assets/images/rur6.jpg" alt="flood image" />
                                                </div>
                                            </div>

                                        </div>


                                    </div>
                                </div>
                                <div class="tab-content-1 " id="btn-10">
                                    <div class="tab-card-section">
                                        <div class="sub-card-section1">
                                            <div class="text-end"> <img src="assets/images/slider2.png" alt="logo" class="img-fluid" /></div>
                                            <div>
                                                <h1>
                                                    Work
                                                    <br></br>
                                                    Together, Seamlessly
                                                </h1>
                                                <p>Assign tasks, share updates, and communicate<br></br> through comments and mentions all within <br></br> a centralized workspace.</p>
                                            </div>
                                        </div>
                                        <div class="sub-card-section2">
                                            <div>
                                                <h1>
                                                    No Silos, <br></br>
                                                    Just Sync
                                                </h1>
                                                <p>Keep your team aligned with real-time updates, <br></br>file sharing, and collaborative task boards that ensure everyone’s<br></br> on the same page.</p>
                                            </div>
                                            <div class="position-relative">
                                                <h3>With Saihatask Collaboration

                                                </h3>
                                                <p>Great teamwork starts with smart tools.</p>
                                                <img src="assets/images/slider1.png" alt="logo" class="img-fluid position-absolute" />
                                            </div>

                                        </div>
                                    </div>
                                    <div class="content-1-1">
                                        <div class="circle-div ">
                                            <div class="text-div"> <img src="assets/images/circle-with-text-lg.png" alt="circle-with-text" class="img-fluid program21" />
                                                <div class="img-div">
                                                    <img src="assets/images/circle-with-text-lg-logo.png" alt="logo" class="img-fluid hand-in-circle" />
                                                </div>
                                            </div>

                                        </div>
                                        <div class="date-div">
                                            <div class="para-1">
                                                <div class="img-div"></div>
                                                <p>New Feature Rollout</p>
                                            </div>


                                        </div>

                                        <div class="loc-div">
                                            <div class="para-1">
                                                <div class="img-div"></div>
                                                <p>Available for All Plans</p>

                                            </div>

                                        </div>

                                        <div class="content-1-2">
                                            <div class="h-div">
                                                <h2>Team Collaboration</h2>
                                            </div>
                                            <p>Collaboration shouldn't feel like chaos. Saihatask brings your team together in one shared workspace where chats, files, and task updates live side by side. Collaborate through in-task discussions, share updates instantly, and keep every stakeholder in sync without switching tools.

                                            </p>
                                            <p>Whether your team is remote, hybrid, or in-office, Saihatask ensures seamless communication, faster decisions, and aligned execution across every project.</p>
                                        </div>

                                        <div class="content-1-3">
                                            <div class="row">
                                                <div class="col-lg-6">
                                                    <div class="img-div123">
                                                        <div class="circle-div circle-div123">
                                                            <div class="text-div"> <img src="assets/images/circle-with-text-sm.png" alt="circle-with-text" class="img-fluid program21" />
                                                                <div class="img-div">
                                                                    <img src="assets/images/circle-with-text-sm-logo.png" alt="logo" class="img-fluid hand-in-circle" />
                                                                </div>
                                                            </div>

                                                        </div>
                                                        <img src="assets/images/tra1.jpg" alt="flood image"
                                                            class="img-fluid" />
                                                    </div>
                                                </div>
                                                <div class="col-lg-6 list-div">
                                                    <h3>Key Benefits</h3>

                                                    <div class="ul-div">
                                                        <ul>
                                                            <li>Chat within the platform to keep conversations focused and contextual


                                                            </li>
                                                            <li>Centralized workspace for discussions, file sharing, and task updates

                                                            </li>
                                                            <li>Collaborate seamlessly across departments using shared boards and dashboards
                                                            </li>
                                                            <li>Enable smooth handovers and accelerate decision-making
                                                            </li>
                                                            <li>Foster a culture of teamwork, clarity, and transparency</li>

                                                        </ul>
                                                    </div>
                                                </div>

                                            </div>


                                        </div>

                                        <div class="content-1-4">
                                            <div class="gallery-section">
                                                <div class="img-div-1">
                                                    <img src="assets/images/urb2.jpg" alt="flood image" />
                                                </div>
                                                <div class="img-div-2">
                                                    <div class="img-div-2-1">
                                                        <img src="assets/images/rul3.jpg" alt="flood image" />
                                                    </div>

                                                    <div class="img-div-2-2">
                                                        <img src="assets/images/rur4.jpg" alt="flood image" />
                                                    </div>
                                                </div>

                                                <div class="img-div-3">
                                                    <img src="assets/images/rur5.jpg" alt="flood image" />
                                                </div>
                                                <div class="img-div-4">
                                                    <img src="assets/images/rur6.jpg" alt="flood image" />
                                                </div>
                                            </div>

                                        </div>


                                    </div>
                                </div>
                                <div class="tab-content-1 " id="btn-11">
                                    <div class="tab-card-section">
                                        <div class="sub-card-section1">
                                            <div class="text-end"> <img src="assets/images/slider2.png" alt="logo" class="img-fluid" /></div>
                                            <div>
                                                <h1>
                                                    14-Day
                                                    <br></br>
                                                    Task View
                                                </h1>
                                                <p>

                                                    View all employee tasks for the next 14 days. Add leaves or holidays, and tasks automatically shift to the next working day for seamless planning.
                                                </p>
                                            </div>
                                        </div>
                                        <div class="sub-card-section2">
                                            <div>
                                                <h1>
                                                    Smart <br></br> Task Shift

                                                </h1>
                                                <p>
                                                    Easily manage leaves and holidays. Tasks of affected days are automatically rescheduled, ensuring workflow continuity with the best task manager software.
                                                </p>
                                            </div>
                                            <div class="position-relative">
                                                <h3>With 14-Day Task View

                                                </h3>
                                                <p>
                                                    Plan Ahead, Work Smarter.
                                                </p>
                                                <img src="assets/images/slider1.png" alt="logo" class="img-fluid position-absolute" />
                                            </div>

                                        </div>
                                    </div>
                                    <div class="content-1-1">
                                        <div class="circle-div ">
                                            <div class="text-div"> <img src="assets/images/circle-with-text-lg.png" alt="circle-with-text" class="img-fluid program21" />
                                                <div class="img-div">
                                                    <img src="assets/images/circle-with-text-lg-logo.png" alt="logo" class="img-fluid hand-in-circle" />
                                                </div>
                                            </div>

                                        </div>
                                        <div class="date-div">
                                            <div class="para-1">
                                                <div class="img-div"></div>
                                                <p>New Feature Rollout</p>
                                            </div>


                                        </div>

                                        <div class="loc-div">
                                            <div class="para-1">
                                                <div class="img-div"></div>
                                                <p>Available for All Plans</p>

                                            </div>

                                        </div>

                                        <div class="content-1-2">
                                            <div class="h-div">
                                                <h2>Monthly Task Planner</h2>
                                            </div>
                                            <p>
                                                Monthly Task Planner gives a clear view of all employee tasks for the next 14 days, helping teams plan ahead with ease. Admins can add leaves and holidays, and the system smartly shifts pending tasks to the next available working day without delays. This ensures smooth workflow, avoids missed deadlines, and keeps productivity intact. A must-have feature in the best task manager software for efficient team management.
                                            </p>
                                        </div>

                                        <div class="content-1-3">
                                            <div class="row">
                                                <div class="col-lg-6">
                                                    <div class="img-div123">
                                                        <div class="circle-div circle-div123">
                                                            <div class="text-div"> <img src="assets/images/circle-with-text-sm.png" alt="circle-with-text" class="img-fluid program21" />
                                                                <div class="img-div">
                                                                    <img src="assets/images/circle-with-text-sm-logo.png" alt="logo" class="img-fluid hand-in-circle" />
                                                                </div>
                                                            </div>

                                                        </div>
                                                        <img src="assets/images/tra1.jpg" alt="flood image"
                                                            class="img-fluid" />
                                                    </div>
                                                </div>
                                                <div class="col-lg-6 list-div">
                                                    <h3>Key Benefits</h3>

                                                    <div class="ul-div">

                                                        <ul>
                                                            <li> Plan and view tasks 14 days ahead with the best task manager software


                                                            </li>
                                                            <li>Auto-shift tasks on leaves or holidays for smooth workflow

                                                            </li>
                                                            <li>  Track deadlines and approvals with admin control

                                                            </li>
                                                            <li>Save time through smart rescheduling
                                                            </li>
                                                            <li> Centralized view for easy planning and reporting</li>

                                                        </ul>
                                                    </div>
                                                </div>

                                            </div>


                                        </div>

                                        <div class="content-1-4">
                                            <div class="gallery-section">
                                                <div class="img-div-1">
                                                    <img src="assets/images/urb2.jpg" alt="flood image" />
                                                </div>
                                                <div class="img-div-2">
                                                    <div class="img-div-2-1">
                                                        <img src="assets/images/rul3.jpg" alt="flood image" />
                                                    </div>

                                                    <div class="img-div-2-2">
                                                        <img src="assets/images/rur4.jpg" alt="flood image" />
                                                    </div>
                                                </div>

                                                <div class="img-div-3">
                                                    <img src="assets/images/rur5.jpg" alt="flood image" />
                                                </div>
                                                <div class="img-div-4">
                                                    <img src="assets/images/rur6.jpg" alt="flood image" />
                                                </div>
                                            </div>

                                        </div>


                                    </div>
                                </div>
                                <div class="tab-content-1 " id="btn-12">
                                    <div class="tab-card-section">
                                        <div class="sub-card-section1">
                                            <div class="text-end"> <img src="assets/images/slider2.png" alt="logo" class="img-fluid" /></div>
                                            <div>
                                                <h1>
                                                    Work
                                                    <br></br>
                                                    Activity Log
                                                </h1>
                                                <p>
                                                    Track when employees start, pause, and complete work. Get detailed daily insights for performance monitoring and workforce transparency.
                                                </p>
                                            </div>
                                        </div>
                                        <div class="sub-card-section2">
                                            <div>
                                                <h1>
                                                    Performance  <br></br>
                                                    Record
                                                </h1>
                                                <p>
                                                    Maintain a clear log of employee activities. Monitor productivity, identify gaps, and streamline reporting with the best task manager software.
                                                </p>
                                            </div>
                                            <div class="position-relative">
                                                <h3>With Employee Report Log

                                                </h3>
                                                <p>
                                                    Clear Insights, Better Control.
                                                </p>
                                                <img src="assets/images/slider1.png" alt="logo" class="img-fluid position-absolute" />
                                            </div>

                                        </div>
                                    </div>
                                    <div class="content-1-1">
                                        <div class="circle-div ">
                                            <div class="text-div"> <img src="assets/images/circle-with-text-lg.png" alt="circle-with-text" class="img-fluid program21" />
                                                <div class="img-div">
                                                    <img src="assets/images/circle-with-text-lg-logo.png" alt="logo" class="img-fluid hand-in-circle" />
                                                </div>
                                            </div>

                                        </div>
                                        <div class="date-div">
                                            <div class="para-1">
                                                <div class="img-div"></div>
                                                <p>New Feature Rollout</p>
                                            </div>


                                        </div>

                                        <div class="loc-div">
                                            <div class="para-1">
                                                <div class="img-div"></div>
                                                <p>Available for All Plans</p>

                                            </div>

                                        </div>

                                        <div class="content-1-2">
                                            <div class="h-div">
                                                <h2>Employee Report</h2>
                                            </div>
                                            <p>
                                                The Employee Report feature provides a detailed log of each employee’s workday, including start times, breaks, pauses, and completion. Managers can monitor activity in real time, ensuring transparency and accountability. This feature helps identify productivity patterns, improve efficiency, and simplify reporting. Integrated with the best task manager software, it creates a clear view of individual and team performance.
                                            </p>
                                        </div>

                                        <div class="content-1-3">
                                            <div class="row">
                                                <div class="col-lg-6">
                                                    <div class="img-div123">
                                                        <div class="circle-div circle-div123">
                                                            <div class="text-div"> <img src="assets/images/circle-with-text-sm.png" alt="circle-with-text" class="img-fluid program21" />
                                                                <div class="img-div">
                                                                    <img src="assets/images/circle-with-text-sm-logo.png" alt="logo" class="img-fluid hand-in-circle" />
                                                                </div>
                                                            </div>

                                                        </div>
                                                        <img src="assets/images/tra1.jpg" alt="flood image"
                                                            class="img-fluid" />
                                                    </div>
                                                </div>
                                                <div class="col-lg-6 list-div">
                                                    <h3>Key Benefits</h3>

                                                    <div class="ul-div">
                                                        <ul>
                                                            <li> Track daily employee work activity with precision
                                                            </li>
                                                            <li>   Gain real-time insights into performance and breaks
                                                            </li>
                                                            <li>   Improve accountability through transparent reporting
                                                            </li>
                                                            <li>   Identify productivity trends and optimize workloads
                                                            </li>
                                                            <li> Centralized record-keeping for easy management
                                                            </li>

                                                        </ul>
                                                    </div>
                                                </div>

                                            </div>


                                        </div>

                                        <div class="content-1-4">
                                            <div class="gallery-section">
                                                <div class="img-div-1">
                                                    <img src="assets/images/urb2.jpg" alt="flood image" />
                                                </div>
                                                <div class="img-div-2">
                                                    <div class="img-div-2-1">
                                                        <img src="assets/images/rul3.jpg" alt="flood image" />
                                                    </div>

                                                    <div class="img-div-2-2">
                                                        <img src="assets/images/rur4.jpg" alt="flood image" />
                                                    </div>
                                                </div>

                                                <div class="img-div-3">
                                                    <img src="assets/images/rur5.jpg" alt="flood image" />
                                                </div>
                                                <div class="img-div-4">
                                                    <img src="assets/images/rur6.jpg" alt="flood image" />
                                                </div>
                                            </div>

                                        </div>


                                    </div>
                                </div>
                                <div class="tab-content-1 " id="btn-13">
                                    <div class="tab-card-section">
                                        <div class="sub-card-section1">
                                            <div class="text-end"> <img src="assets/images/slider2.png" alt="logo" class="img-fluid" /></div>
                                            <div>
                                                <h1>
                                                    User
                                                    <br></br>
                                                    Privileges                                                </h1>
                                                <p>
                                                    Assign and manage user privileges with ease. Control access based on designation, ensuring security and smooth workflow across teams.
                                                </p>
                                            </div>
                                        </div>
                                        <div class="sub-card-section2">
                                            <div>
                                                <h1>
                                                    Secure<br></br>
                                                    Access Rules
                                                </h1>
                                                <p>
                                                    Define rules for each role and manage permissions. Keep data safe while empowering employees with only the access they need.
                                                </p>
                                            </div>
                                            <div class="position-relative">
                                                <h3>With Role-Based Access
                                                </h3>
                                                <p>
                                                    Secure Privileges, Smarter Control.
                                                </p>
                                                <img src="assets/images/slider1.png" alt="logo" class="img-fluid position-absolute" />
                                            </div>

                                        </div>
                                    </div>
                                    <div class="content-1-1">
                                        <div class="circle-div ">
                                            <div class="text-div"> <img src="assets/images/circle-with-text-lg.png" alt="circle-with-text" class="img-fluid program21" />
                                                <div class="img-div">
                                                    <img src="assets/images/circle-with-text-lg-logo.png" alt="logo" class="img-fluid hand-in-circle" />
                                                </div>
                                            </div>

                                        </div>
                                        <div class="date-div">
                                            <div class="para-1">
                                                <div class="img-div"></div>
                                                <p>New Feature Rollout</p>
                                            </div>


                                        </div>

                                        <div class="loc-div">
                                            <div class="para-1">
                                                <div class="img-div"></div>
                                                <p>Available for All Plans</p>

                                            </div>

                                        </div>

                                        <div class="content-1-2">
                                            <div class="h-div">
                                                <h2>Role-Based Access</h2>
                                            </div>
                                            <p>
                                                The Role-Based Access feature allows businesses to manage user privileges effectively by linking them to designations and their associated rules. Each user is assigned specific rights, ensuring they only access what’s relevant to their role. This structured approach enhances data security, reduces errors, and streamlines workflows. By aligning roles with permissions, organizations maintain control while enabling teams to work more efficiently.
                                            </p>
                                        </div>

                                        <div class="content-1-3">
                                            <div class="row">
                                                <div class="col-lg-6">
                                                    <div class="img-div123">
                                                        <div class="circle-div circle-div123">
                                                            <div class="text-div"> <img src="assets/images/circle-with-text-sm.png" alt="circle-with-text" class="img-fluid program21" />
                                                                <div class="img-div">
                                                                    <img src="assets/images/circle-with-text-sm-logo.png" alt="logo" class="img-fluid hand-in-circle" />
                                                                </div>
                                                            </div>

                                                        </div>
                                                        <img src="assets/images/tra1.jpg" alt="flood image"
                                                            class="img-fluid" />
                                                    </div>
                                                </div>
                                                <div class="col-lg-6 list-div">
                                                    <h3>Key Benefits</h3>

                                                    <div class="ul-div">
                                                        <ul>
                                                            <li>Assign and manage access based on roles and designations
                                                            </li>
                                                            <li>Centralized workspace for discussions, file sharing, and task updates
                                                            </li>
                                                            <li>  Simplify user management with predefined access rules
                                                            </li>
                                                            <li>  Improve efficiency by giving employees only what they need
                                                            </li>
                                                            <li>  Centralized control for seamless administration
                                                            </li>
                                                        </ul>
                                                    </div>
                                                </div>

                                            </div>


                                        </div>

                                        <div class="content-1-4">
                                            <div class="gallery-section">
                                                <div class="img-div-1">
                                                    <img src="assets/images/urb2.jpg" alt="flood image" />
                                                </div>
                                                <div class="img-div-2">
                                                    <div class="img-div-2-1">
                                                        <img src="assets/images/rul3.jpg" alt="flood image" />
                                                    </div>

                                                    <div class="img-div-2-2">
                                                        <img src="assets/images/rur4.jpg" alt="flood image" />
                                                    </div>
                                                </div>

                                                <div class="img-div-3">
                                                    <img src="assets/images/rur5.jpg" alt="flood image" />
                                                </div>
                                                <div class="img-div-4">
                                                    <img src="assets/images/rur6.jpg" alt="flood image" />
                                                </div>
                                            </div>

                                        </div>


                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* <div class="pricing-section" id="pricing">
                        <div class="pricing-head">
                            <h2>Saihatask <br></br> Pricing Plan</h2>
                            <p>Explore flexible pricing tailored to your team’s <br></br> needs— our best task and project management software is designed to deliver value, boost<br></br>  productivity, and scale with your growth.</p>
                        </div>
                        <div class="pricing-card">
                            <div class="pricing-left">
                                <div class="price-box">
                                    <p class="plan">Standard</p>
                                    <h1>₹15</h1> 
                                    <p class="per">per head / day</p>
                                    <button id="donate2-now" class="donate-btn btn-shake btn-start mb-3"><a href="#contact-id">Book Demo</a></button>
                                    <p class="min">*Minimum 20 users</p>
                                    <p class="min">*Customization charges vary as per needs</p>
                                </div>
                            </div>
                            <div class="pricing-right">
                                <p class="features-heading">
                                    Access these features when you get this pricing package for your business.
                                </p>
                                <ul class="features-list">



                                    <li>Task creation & assignments</li>
                                    <li>  Employee Performance Reports</li>
                                    <li>Instant Status Updates and Reminders</li>
                                    <li>Role Based Dashboards</li>
                                    <li>Monthly Task Overview</li>
                                    <li>Daily Reports</li>
                                </ul>
                            </div>
                        </div>
                    </div> */}




                    <div class="home-swiper">
                        <div class="swiper-section">
                            <div class="swiper-container" id="section-5-swiper" data-scroll-ignore="true">
                                <div class="swiper-wrapper">
                                    <div class="swiper-slide">
                                        <div class="swiper-slide-content">
                                            <div class="msg-icon-div">
                                                <div class="img-div">
                                                    <img src="assets/images/message-icon.png" alt="message-icon" />
                                                </div>
                                            </div>
                                            <div class="img-section">
                                                <img src="assets/images/victoria.jpg" alt="people image" />
                                            </div>

                                            <div class="content-1">
                                                <div class="star-div">
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                </div>
                                                <p class="para-1">
                                                    "Saihatask delivered exactly what we needed  flexible, efficient, and perfectly aligned with our
                                                    evolving workflows. As one of the best task manager software platforms we've used, it has
                                                    streamlined our operations significantly. The team has been responsive and professional, making
                                                    collaboration smooth and impactful. We're excited to grow alongside the platform."
                                                </p>
                                                <p class="para-2"> <span><b>Geethu</b></span></p>
                                                <p class="para-2"> <span>Asst. Manager - Compliance & Administration</span></p>
                                                <img src="assets/images/victoria.png" alt="star icon" />


                                            </div>
                                        </div>
                                    </div>
                                    <div class="swiper-slide">
                                        <div class="swiper-slide-content">
                                            <div class="msg-icon-div">
                                                <div class="img-div">
                                                    <img src="assets/images/message-icon.png" alt="message-icon" />
                                                </div>
                                            </div>
                                            <div class="img-section">
                                                <img src="assets/images/redteam.jpg" alt="people image" />
                                            </div>

                                            <div class="content-1">
                                                <div class="star-div">
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                </div>
                                                <p class="para-1">"
                                                    We didn't just organize tasks we transformed how we operate. Saihatask, a powerful task
                                                    management software, gave us laser-sharp visibility, discipline in execution, and a rhythm to our
                                                    workflow we didn't know we were missing. It's now the backbone of our daily operations.
                                                    "</p>
                                                <p class="para-2"> <span><b>Jaizal Ali</b></span></p>
                                                <p class="para-2"> <span>CEO</span></p>
                                                <img src="assets/images/redteam.png" alt="star icon" />


                                            </div>
                                        </div>
                                    </div>

                                    <div class="swiper-slide">
                                        <div class="swiper-slide-content">
                                            <div class="msg-icon-div">
                                                <div class="img-div">
                                                    <img src="assets/images/message-icon.png" alt="message-icon" />
                                                </div>
                                            </div>
                                            <div class="img-section">
                                                <img src="assets/images/storysphere.jpg" alt="people image" />
                                            </div>

                                            <div class="content-1">
                                                <div class="star-div">
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                </div>
                                                <p class="para-1">" Managing creative chaos felt impossible until Saihatask. As a powerful team collaboration
                                                    software, it turned our scattered tasks into a synchronized workflow. Clients are happier,
                                                    deadlines are met, and our team finally breathes easy. It's the best decision we made this year.
                                                    "</p>
                                                <p class="para-2"> <span><b>Rakesh R</b></span></p>
                                                <p class="para-2"> <span>CEO</span></p>
                                                <img src="assets/images/storysphere.png" alt="star icon" />


                                            </div>
                                        </div>
                                    </div>


                                    <div class="swiper-slide">
                                        <div class="swiper-slide-content">
                                            <div class="msg-icon-div">
                                                <div class="img-div">
                                                    <img src="assets/images/message-icon.png" alt="message-icon" />
                                                </div>
                                            </div>
                                            <div class="img-section">
                                                <img src="assets/images/storysphere 1.png" alt="people image" />
                                            </div>

                                            <div class="content-1">
                                                <div class="star-div">
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                </div>
                                                <p class="para-1">" Very useful for managing workers’ day-to-day tasks and activities. It is user-friendly, easy to navigate, and makes it simple to assign, track, and manage work efficiently. It has helped us stay organized and keep better track of our team’s daily activities.
                                                    "</p>
                                                <p class="para-2"> <span><b>Dileep P V</b></span></p>
                                                <p class="para-2"> <span>Founder and Managing Director - Capital Gains Associates Pvt. Ltd.</span></p>
                                                <img src="assets/images/cg.png" alt="star icon" />


                                            </div>
                                        </div>
                                    </div>

                                    <div class="swiper-slide">
                                        <div class="swiper-slide-content">
                                            <div class="msg-icon-div">
                                                <div class="img-div">
                                                    <img src="assets/images/message-icon.png" alt="message-icon" />
                                                </div>
                                            </div>
                                            <div class="img-section">
                                                <img src="assets/images/victoria.jpg" alt="people image" />
                                            </div>

                                            <div class="content-1">
                                                <div class="star-div">
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                </div>
                                                <p class="para-1">
                                                    "Saihatask delivered exactly what we needed flexible, efficient, and perfectly aligned with our
                                                    evolving workflows. As one of the best task manager software platforms we've used, it has
                                                    streamlined our operations significantly. The team has been responsive and professional, making
                                                    collaboration smooth and impactful. We're excited to grow alongside the platform."
                                                </p>
                                                <p class="para-2"> <span><b>Geethu</b></span></p>
                                                <p class="para-2"> <span>Asst. Manager - Compliance & Administration</span></p>
                                                <img src="assets/images/victoria.png" alt="star icon" />

                                            </div>
                                        </div>
                                    </div>
                                    <div class="swiper-slide">
                                        <div class="swiper-slide-content">
                                            <div class="msg-icon-div">
                                                <div class="img-div">
                                                    <img src="assets/images/message-icon.png" alt="message-icon" />
                                                </div>
                                            </div>
                                            <div class="img-section">
                                                <img src="assets/images/redteam.jpg" alt="people image" />
                                            </div>

                                            <div class="content-1">
                                                <div class="star-div">
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                </div>
                                                <p class="para-1">"
                                                    We didn't just organize tasks we transformed how we operate. Saihatask, a powerful task
                                                    management software, gave us laser-sharp visibility, discipline in execution, and a rhythm to our
                                                    workflow we didn't know we were missing. It's now the backbone of our daily operations.
                                                    "</p>
                                                <p class="para-2"> <span><b>Jaizal Ali</b></span></p>
                                                <p class="para-2"> <span>CEO</span></p>
                                                <img src="assets/images/redteam.png" alt="star icon" />

                                            </div>
                                        </div>
                                    </div>

                                    <div class="swiper-slide">
                                        <div class="swiper-slide-content">
                                            <div class="msg-icon-div">
                                                <div class="img-div">
                                                    <img src="assets/images/message-icon.png" alt="message-icon" />
                                                </div>
                                            </div>
                                            <div class="img-section">
                                                <img src="assets/images/storysphere.jpg" alt="people image" />
                                            </div>

                                            <div class="content-1">
                                                <div class="star-div">
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                </div>
                                                <p class="para-1">" Managing creative chaos felt impossible  until Saihatask. As a powerful team collaboration
                                                    software, it turned our scattered tasks into a synchronized workflow. Clients are happier,
                                                    deadlines are met, and our team finally breathes easy. It's the best decision we made this year.
                                                    "</p>
                                                <p class="para-2"> <span><b>Rakesh R</b></span></p>
                                                <p class="para-2"> <span>CEO</span></p>
                                                <img src="assets/images/storysphere.png" alt="star icon" />


                                            </div>
                                        </div>
                                    </div>

                                    <div class="swiper-slide">
                                        <div class="swiper-slide-content">
                                            <div class="msg-icon-div">
                                                <div class="img-div">
                                                    <img src="assets/images/message-icon.png" alt="message-icon" />
                                                </div>
                                            </div>
                                            <div class="img-section">
                                                <img src="assets/images/storysphere 1.png" alt="people image" />
                                            </div>

                                            <div class="content-1">
                                                <div class="star-div">
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                </div>
                                                <p class="para-1">" Very useful for managing workers’ day-to-day tasks and activities. It is user-friendly, easy to navigate, and makes it simple to assign, track, and manage work efficiently. It has helped us stay organized and keep better track of our team’s daily activities.
                                                    "</p>
                                                <p class="para-2"> <span><b>Dileep P V</b></span></p>
                                                <p class="para-2"> <span>Founder and Managing Director - Capital Gains Associates Pvt. Ltd.</span></p>
                                                <img src="assets/images/cg.png" alt="star icon"  />


                                            </div>
                                        </div>
                                    </div>


                                    <div class="swiper-slide">
                                        <div class="swiper-slide-content">
                                            <div class="msg-icon-div">
                                                <div class="img-div">
                                                    <img src="assets/images/message-icon.png" alt="message-icon" />
                                                </div>
                                            </div>
                                            <div class="img-section">
                                                <img src="assets/images/victoria.jpg" alt="people image" />
                                            </div>

                                            <div class="content-1">
                                                <div class="star-div">
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                </div>
                                                <p class="para-1">
                                                    "Saihatask delivered exactly what we needed   flexible, efficient, and perfectly aligned with our
                                                    evolving workflows. As one of the best task manager software platforms we've used, it has
                                                    streamlined our operations significantly. The team has been responsive and professional, making
                                                    collaboration smooth and impactful. We're excited to grow alongside the platform."
                                                </p>
                                                <p class="para-2"> <span><b>Geethu</b></span></p>
                                                <p class="para-2"> <span>Asst. Manager - Compliance & Administration</span></p>
                                                <img src="assets/images/victoria.png" alt="star icon" />


                                            </div>
                                        </div>
                                    </div>
                                    <div class="swiper-slide">
                                        <div class="swiper-slide-content">
                                            <div class="msg-icon-div">
                                                <div class="img-div">
                                                    <img src="assets/images/message-icon.png" alt="message-icon" />
                                                </div>
                                            </div>
                                            <div class="img-section">
                                                <img src="assets/images/redteam.jpg" alt="people image" />
                                            </div>

                                            <div class="content-1">
                                                <div class="star-div">
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                </div>
                                                <p class="para-1">"
                                                    We didn't just organize tasks we transformed how we operate. Saihatask, a powerful task
                                                    management software, gave us laser-sharp visibility, discipline in execution, and a rhythm to our
                                                    workflow we didn't know we were missing. It's now the backbone of our daily operations.
                                                    "</p>
                                                <p class="para-2"> <span><b>Jaizal Ali</b></span></p>
                                                <p class="para-2"> <span>CEO</span></p>
                                                <img src="assets/images/redteam.png" alt="star icon" />


                                            </div>
                                        </div>
                                    </div>

                                    <div class="swiper-slide">
                                        <div class="swiper-slide-content">
                                            <div class="msg-icon-div">
                                                <div class="img-div">
                                                    <img src="assets/images/message-icon.png" alt="message-icon" />
                                                </div>
                                            </div>
                                            <div class="img-section">
                                                <img src="assets/images/storysphere.jpg" alt="people image" />
                                            </div>

                                            <div class="content-1">
                                                <div class="star-div">
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                </div>
                                                <p class="para-1">" Managing creative chaos felt impossible   until Saihatask. As a powerful team collaboration
                                                    software, it turned our scattered tasks into a synchronized workflow. Clients are happier,
                                                    deadlines are met, and our team finally breathes easy. It's the best decision we made this year.
                                                    "</p>
                                                <p class="para-2"> <span><b>Rakesh R</b></span></p>
                                                <p class="para-2"> <span>CEO</span></p>
                                                <img src="assets/images/storysphere.png" alt="star icon" />

                                            </div>
                                        </div>
                                    </div>

                                    <div class="swiper-slide">
                                        <div class="swiper-slide-content">
                                            <div class="msg-icon-div">
                                                <div class="img-div">
                                                    <img src="assets/images/message-icon.png" alt="message-icon" />
                                                </div>
                                            </div>
                                            <div class="img-section">
                                                <img src="assets/images/storysphere 1.png" alt="people image" />
                                            </div>

                                            <div class="content-1">
                                                <div class="star-div">
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                </div>
                                                <p class="para-1">" Very useful for managing workers’ day-to-day tasks and activities. It is user-friendly, easy to navigate, and makes it simple to assign, track, and manage work efficiently. It has helped us stay organized and keep better track of our team’s daily activities.
                                                    "</p>
                                                <p class="para-2"> <span><b>Dileep P V</b></span></p>
                                                <p class="para-2"> <span>Founder and Managing Director - Capital Gains Associates Pvt. Ltd.</span></p>
                                                <img src="assets/images/cg.png" alt="star icon" />


                                            </div>
                                        </div>
                                    </div>

                                    <div class="swiper-slide">
                                        <div class="swiper-slide-content">
                                            <div class="msg-icon-div">
                                                <div class="img-div">
                                                    <img src="assets/images/message-icon.png" alt="message-icon" />
                                                </div>
                                            </div>
                                            <div class="img-section">
                                                <img src="assets/images/victoria.jpg" alt="people image" />
                                            </div>

                                            <div class="content-1">
                                                <div class="star-div">
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                </div>
                                                <p class="para-1">
                                                    "Saihatask delivered exactly what we needed   flexible, efficient, and perfectly aligned with our
                                                    evolving workflows. As one of the best task manager software platforms we've used, it has
                                                    streamlined our operations significantly. The team has been responsive and professional, making
                                                    collaboration smooth and impactful. We're excited to grow alongside the platform."
                                                </p>
                                                <p class="para-2"> <span><b>Geethu</b></span></p>
                                                <p class="para-2"> <span>Asst. Manager - Compliance & Administration</span></p>
                                                <img src="assets/images/victoria.png" alt="star icon" />


                                            </div>
                                        </div>
                                    </div>
                                    <div class="swiper-slide">
                                        <div class="swiper-slide-content">
                                            <div class="msg-icon-div">
                                                <div class="img-div">
                                                    <img src="assets/images/message-icon.png" alt="message-icon" />
                                                </div>
                                            </div>
                                            <div class="img-section">
                                                <img src="assets/images/redteam.jpg" alt="people image" />
                                            </div>

                                            <div class="content-1">
                                                <div class="star-div">
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                </div>
                                                <p class="para-1">"
                                                    We didn't just organize tasks we transformed how we operate. Saihatask, a powerful task
                                                    management software, gave us laser-sharp visibility, discipline in execution, and a rhythm to our
                                                    workflow we didn't know we were missing. It's now the backbone of our daily operations.
                                                    "</p>
                                                <p class="para-2"> <span><b>Jaizal Ali</b></span></p>
                                                <p class="para-2"> <span>CEO</span></p>
                                                <img src="assets/images/redteam.png" alt="star icon" />


                                            </div>
                                        </div>
                                    </div>

                                    <div class="swiper-slide">
                                        <div class="swiper-slide-content">
                                            <div class="msg-icon-div">
                                                <div class="img-div">
                                                    <img src="assets/images/message-icon.png" alt="message-icon" />
                                                </div>
                                            </div>
                                            <div class="img-section">
                                                <img src="assets/images/storysphere.jpg" alt="people image" />
                                            </div>

                                            <div class="content-1">
                                                <div class="star-div">
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                </div>
                                                <p class="para-1">" Managing creative chaos felt impossible   until Saihatask. As a powerful team collaboration
                                                    software, it turned our scattered tasks into a synchronized workflow. Clients are happier,
                                                    deadlines are met, and our team finally breathes easy. It's the best decision we made this year.
                                                    "</p>
                                                <p class="para-2"> <span><b>Rakesh R</b></span></p>
                                                <p class="para-2"> <span>CEO</span></p>
                                                <img src="assets/images/storysphere.png" alt="star icon" />


                                            </div>
                                        </div>
                                    </div>

                                    <div class="swiper-slide">
                                        <div class="swiper-slide-content">
                                            <div class="msg-icon-div">
                                                <div class="img-div">
                                                    <img src="assets/images/message-icon.png" alt="message-icon" />
                                                </div>
                                            </div>
                                            <div class="img-section">
                                                <img src="assets/images/storysphere 1.png" alt="people image" />
                                            </div>

                                            <div class="content-1">
                                                <div class="star-div">
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                </div>
                                                <p class="para-1">" Very useful for managing workers’ day-to-day tasks and activities. It is user-friendly, easy to navigate, and makes it simple to assign, track, and manage work efficiently. It has helped us stay organized and keep better track of our team’s daily activities.
                                                    "</p>
                                                <p class="para-2"> <span><b>Dileep P V</b></span></p>
                                                <p class="para-2"> <span>Founder and Managing Director - Capital Gains Associates Pvt. Ltd.</span></p>
                                                <img src="assets/images/cg.png" alt="star icon" />


                                            </div>
                                        </div>
                                    </div>

                                    <div class="swiper-slide">
                                        <div class="swiper-slide-content">
                                            <div class="msg-icon-div">
                                                <div class="img-div">
                                                    <img src="assets/images/message-icon.png" alt="message-icon" />
                                                </div>
                                            </div>
                                            <div class="img-section">
                                                <img src="assets/images/victoria.jpg" alt="people image" />
                                            </div>

                                            <div class="content-1">
                                                <div class="star-div">
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                </div>
                                                <p class="para-1">
                                                    "Saihatask delivered exactly what we needed  flexible, efficient, and perfectly aligned with our
                                                    evolving workflows. As one of the best task manager software platforms we've used, it has
                                                    streamlined our operations significantly. The team has been responsive and professional, making
                                                    collaboration smooth and impactful. We're excited to grow alongside the platform."
                                                </p>
                                                <p class="para-2"> <span><b>Geethu</b></span></p>
                                                <p class="para-2"> <span>Asst. Manager - Compliance & Administration</span></p>
                                                <img src="assets/images/victoria.png" alt="star icon" />

                                            </div>
                                        </div>
                                    </div>
                                    <div class="swiper-slide">
                                        <div class="swiper-slide-content">
                                            <div class="msg-icon-div">
                                                <div class="img-div">
                                                    <img src="assets/images/message-icon.png" alt="message-icon" />
                                                </div>
                                            </div>
                                            <div class="img-section">
                                                <img src="assets/images/redteam.jpg" alt="people image" />
                                            </div>

                                            <div class="content-1">
                                                <div class="star-div">
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                </div>
                                                <p class="para-1">"
                                                    We didn't just organize tasks we transformed how we operate. Saihatask, a powerful task
                                                    management software, gave us laser-sharp visibility, discipline in execution, and a rhythm to our
                                                    workflow we didn't know we were missing. It's now the backbone of our daily operations.
                                                    "</p>

                                                <p class="para-2"> <span><b>Jaizal Ali</b></span></p>
                                                <p class="para-2"> <span>CEO</span></p>
                                                <img src="assets/images/redteam.png" alt="star icon" />


                                            </div>
                                        </div>
                                    </div>

                                    <div class="swiper-slide">
                                        <div class="swiper-slide-content">
                                            <div class="msg-icon-div">
                                                <div class="img-div">
                                                    <img src="assets/images/message-icon.png" alt="message-icon" />
                                                </div>
                                            </div>
                                            <div class="img-section">
                                                <img src="assets/images/storysphere.jpg" alt="people image" />
                                            </div>

                                            <div class="content-1">
                                                <div class="star-div">
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                </div>
                                                <p class="para-1">"Managing creative chaos felt impossible until Saihatask. As a powerful team collaboration
                                                    software, it turned our scattered tasks into a synchronized workflow. Clients are happier,
                                                    deadlines are met, and our team finally breathes easy. It's the best decision we made this year."</p>
                                                <p class="para-2"> <span><b>Rakesh R</b></span></p>
                                                <p class="para-2"> <span>CEO</span></p>
                                                <img src="assets/images/storysphere.png" alt="star icon" />


                                            </div>
                                        </div>
                                    </div>

                                    <div class="swiper-slide">
                                        <div class="swiper-slide-content">
                                            <div class="msg-icon-div">
                                                <div class="img-div">
                                                    <img src="assets/images/message-icon.png" alt="message-icon" />
                                                </div>
                                            </div>
                                            <div class="img-section">
                                                <img src="assets/images/storysphere 1.png" alt="people image" />
                                            </div>

                                            <div class="content-1">
                                                <div class="star-div">
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                </div>
                                                <p class="para-1">" Very useful for managing workers’ day-to-day tasks and activities. It is user-friendly, easy to navigate, and makes it simple to assign, track, and manage work efficiently. It has helped us stay organized and keep better track of our team’s daily activities.
                                                    "</p>
                                                <p class="para-2"> <span><b>Dileep P V</b></span></p>
                                                <p class="para-2"> <span>Founder and Managing Director - Capital Gains Associates Pvt. Ltd.</span></p>
                                                <img src="assets/images/cg.png" alt="star icon" />


                                            </div>
                                        </div>
                                    </div>

                                    {/* <div class="swiper-slide">
                                        <div class="swiper-slide-content">
                                            <div class="msg-icon-div">
                                                <div class="img-div">
                                                    <img src="assets/images/message-icon.png" alt="" />
                                                </div>
                                            </div>
                                            <div class="img-section">
                                                <img src="assets/images/victoria.jpg" alt="people image" />
                                            </div>

                                            <div class="content-1">
                                                <div class="star-div">
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                </div>
                                                <p class="para-1">"
                                                    Saihataskdelivered exactly what we needed—flexible, efficient, and perfectly aligned with our evolving workflows. The team has been responsive and professional, making collaboration smooth and impactful. We’re excited to grow alongside the platform
                                                    "</p>
                                                <p class="para-2"> <span>Victoria Realtors</span></p>


                                            </div>
                                        </div>
                                    </div>
                                    <div class="swiper-slide">
                                        <div class="swiper-slide-content">
                                            <div class="msg-icon-div">
                                                <div class="img-div">
                                                    <img src="assets/images/message-icon.png" alt="" />
                                                </div>
                                            </div>
                                            <div class="img-section">
                                                <img src="assets/images/swiper-5.png" alt="people image" />
                                            </div>

                                            <div class="content-1">
                                                <div class="star-div">
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                </div>
                                           <p class="para-1">"Managing creative chaos felt impossible—until Saihatask. It turned our scattered tasks into a synchronized workflow. Clients are happier, deadlines are met, and our team finally breathes easy. It’s the best decision we made this year"</p>
                                                <p class="para-2"> <span>Story Sphere</span></p>


                                            </div>
                                        </div>
                                    </div>
                                    <div class="swiper-slide">
                                        <div class="swiper-slide-content">
                                            <div class="msg-icon-div">
                                                <div class="img-div">
                                                    <img src="assets/images/message-icon.png" alt="" />
                                                </div>
                                            </div>
                                            <div class="img-section">
                                                <img src="assets/images/redteam.jpg" alt="people image" />
                                            </div>

                                            <div class="content-1">
                                                <div class="star-div">
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                </div>
                                                <p class="para-1">"
                                                    Thank you for the great task management software. It’s been very helpful for our work and easy to use.
                                                    Special thanks to Ashmi and the team for being quick and helpful.
                                                    "</p>
                                                <p class="para-2"> <span>Geethu (Asst. Manager)</span></p>
                                            </div>
                                        </div>
                                    </div>

                                    <div class="swiper-slide">
                                        <div class="swiper-slide-content">
                                            <div class="msg-icon-div">
                                                <div class="img-div">
                                                    <img src="assets/images/message-icon.png" alt="" />
                                                </div>
                                            </div>
                                            <div class="img-section">
                                                <img src="assets/images/storysphere.jpg" alt="people image" />
                                            </div>

                                            <div class="content-1">
                                                <div class="star-div">
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                </div>
                                                 <p class="para-1">"Managing creative chaos felt impossible—until Saihatask. It turned our scattered tasks into a synchronized workflow. Clients are happier, deadlines are met, and our team finally breathes easy. It’s the best decision we made this year"</p>
                                                <p class="para-2"> <span>Story Sphere</span></p>



                                            </div>
                                        </div>
                                    </div>
                                    <div class="swiper-slide">
                                        <div class="swiper-slide-content">
                                            <div class="msg-icon-div">
                                                <div class="img-div">
                                                    <img src="assets/images/message-icon.png" alt="" />
                                                </div>
                                            </div>
                                            <div class="img-section">
                                                <img src="assets/images/victoria.jpg" alt="people image" />
                                            </div>

                                            <div class="content-1">
                                                <div class="star-div">
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                </div>
                                                 <p class="para-1">"
                                                    Saihataskdelivered exactly what we needed—flexible, efficient, and perfectly aligned with our evolving workflows. The team has been responsive and professional, making collaboration smooth and impactful. We’re excited to grow alongside the platform
                                                    "</p>
                                                <p class="para-2"> <span>Victoria Realtors</span></p>

                                            </div>
                                        </div>
                                    </div>

                                    <div class="swiper-slide">
                                        <div class="swiper-slide-content">
                                            <div class="msg-icon-div">
                                                <div class="img-div">
                                                    <img src="assets/images/message-icon.png" alt="" />
                                                </div>
                                            </div>
                                            <div class="img-section">
                                                <img src="assets/images/redteam.jpg" alt="people image" />
                                            </div>

                                            <div class="content-1">
                                                <div class="star-div">
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                </div>
                                              <p class="para-1">"Managing creative chaos felt impossible—until Saihatask. It turned our scattered tasks into a synchronized workflow. Clients are happier, deadlines are met, and our team finally breathes easy. It’s the best decision we made this year"</p>
                                                <p class="para-2"> <span>Story Sphere</span></p>


                                            </div>
                                        </div>
                                    </div>
                                    <div class="swiper-slide">
                                        <div class="swiper-slide-content">
                                            <div class="msg-icon-div">
                                                <div class="img-div">
                                                    <img src="assets/images/message-icon.png" alt="" />
                                                </div>
                                            </div>
                                            <div class="img-section">
                                                <img src="assets/images/swiper-5.png" alt="people image" />
                                            </div>

                                            <div class="content-1">
                                                <div class="star-div">
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                </div>
                                                <p class="para-1">"
                                                    Thank you for the great task management software. It’s been very helpful for our work and easy to use.
                                                    Special thanks to Ashmi and the team for being quick and helpful.
                                                    "</p>
                                                <p class="para-2"> <span>Geethu (Asst. Manager)</span></p>

                                            </div>
                                        </div>
                                    </div>
                                    <div class="swiper-slide">
                                        <div class="swiper-slide-content">
                                            <div class="msg-icon-div">
                                                <div class="img-div">
                                                    <img src="assets/images/message-icon.png" alt="" />
                                                </div>
                                            </div>
                                            <div class="img-section">
                                                <img src="assets/images/storysphere.jpg" alt="people image" />
                                            </div>

                                            <div class="content-1">
                                                <div class="star-div">
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                </div>
                                               <p class="para-1">"Managing creative chaos felt impossible—until Saihatask. It turned our scattered tasks into a synchronized workflow. Clients are happier, deadlines are met, and our team finally breathes easy. It’s the best decision we made this year"</p>
                                                <p class="para-2"> <span>Story Sphere</span></p>


                                            </div>
                                        </div>
                                    </div>
                                    <div class="swiper-slide">
                                        <div class="swiper-slide-content">
                                            <div class="msg-icon-div">
                                                <div class="img-div">
                                                    <img src="assets/images/message-icon.png" alt="" />
                                                </div>
                                            </div>
                                            <div class="img-section">
                                                <img src="assets/images/victoria.jpg" alt="people image" />
                                            </div>

                                            <div class="content-1">
                                                <div class="star-div">
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                </div>
                                               <p class="para-1">"
                                                    Saihataskdelivered exactly what we needed—flexible, efficient, and perfectly aligned with our evolving workflows. The team has been responsive and professional, making collaboration smooth and impactful. We’re excited to grow alongside the platform
                                                    "</p>
                                                <p class="para-2"> <span>Victoria Realtors</span></p>


                                            </div>
                                        </div>
                                    </div>
                                    <div class="swiper-slide">
                                        <div class="swiper-slide-content">
                                            <div class="msg-icon-div">
                                                <div class="img-div">
                                                    <img src="assets/images/message-icon.png" alt="" />
                                                </div>
                                            </div>
                                            <div class="img-section">
                                                <img src="assets/images/swiper-5.png" alt="people image" />
                                            </div>

                                            <div class="content-1">
                                                <div class="star-div">
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                </div>
                                              <p class="para-1">"Managing creative chaos felt impossible—until Saihatask. It turned our scattered tasks into a synchronized workflow. Clients are happier, deadlines are met, and our team finally breathes easy. It’s the best decision we made this year"</p>
                                                <p class="para-2"> <span>Story Sphere</span></p>



                                            </div>
                                        </div>
                                    </div>
                                    <div class="swiper-slide">
                                        <div class="swiper-slide-content">
                                            <div class="msg-icon-div">
                                                <div class="img-div">
                                                    <img src="assets/images/message-icon.png" alt="" />
                                                </div>
                                            </div>
                                            <div class="img-section">
                                                <img src="assets/images/redteam.jpg" alt="people image" />
                                            </div>

                                            <div class="content-1">
                                                <div class="star-div">
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                </div>
                                                <p class="para-1">"
                                                    Thank you for the great task management software. It’s been very helpful for our work and easy to use.
                                                    Special thanks to Ashmi and the team for being quick and helpful.
                                                    "</p>
                                                <p class="para-2"> <span>Geethu (Asst. Manager)</span></p>


                                            </div>
                                        </div>
                                    </div>

                                    <div class="swiper-slide">
                                        <div class="swiper-slide-content">
                                            <div class="msg-icon-div">
                                                <div class="img-div">
                                                    <img src="assets/images/message-icon.png" alt="" />
                                                </div>
                                            </div>
                                            <div class="img-section">
                                                <img src="assets/images/storysphere.jpg" alt="people image" />
                                            </div>

                                            <div class="content-1">
                                                <div class="star-div">
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                </div>
                                                <p class="para-1">"Managing creative chaos felt impossible—until Saihatask. It turned our scattered tasks into a synchronized workflow. Clients are happier, deadlines are met, and our team finally breathes easy. It’s the best decision we made this year"</p>
                                                <p class="para-2"> <span>Story Sphere</span></p>



                                            </div>
                                        </div>
                                    </div>
                                    <div class="swiper-slide">
                                        <div class="swiper-slide-content">
                                            <div class="msg-icon-div">
                                                <div class="img-div">
                                                    <img src="assets/images/message-icon.png" alt="" />
                                                </div>
                                            </div>
                                            <div class="img-section">
                                                <img src="assets/images/victoria.jpg" alt="people image" />
                                            </div>

                                            <div class="content-1">
                                                <div class="star-div">
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                </div>
                                               <p class="para-1">"
                                                    Saihataskdelivered exactly what we needed—flexible, efficient, and perfectly aligned with our evolving workflows. The team has been responsive and professional, making collaboration smooth and impactful. We’re excited to grow alongside the platform
                                                    "</p>
                                                <p class="para-2"> <span>Victoria Realtors</span></p>


                                            </div>
                                        </div>
                                    </div>
                                    <div class="swiper-slide">
                                        <div class="swiper-slide-content">
                                            <div class="msg-icon-div">
                                                <div class="img-div">
                                                    <img src="assets/images/message-icon.png" alt="" />
                                                </div>
                                            </div>
                                            <div class="img-section">
                                                <img src="assets/images/swiper-5.png" alt="people image" />
                                            </div>

                                            <div class="content-1">
                                                <div class="star-div">
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                    <img src="assets/images/star-icon.png" alt="star icon" />
                                                </div>
                                               <p class="para-1">"Managing creative chaos felt impossible—until Saihatask. It turned our scattered tasks into a synchronized workflow. Clients are happier, deadlines are met, and our team finally breathes easy. It’s the best decision we made this year"</p>
                                                <p class="para-2"> <span>Story Sphere</span></p>


                                            </div>
                                        </div>
                                    </div> */}

                                </div>


                            </div>


                        </div>
                    </div>
                </div>



                <div class="donate-main-section">
                    <div class="donate-details d-flex align-items-center justify-content-between">
                        <div class="donate-first-sec">
                            <h1>Start Managing
                                <br></br>Tasks Smarter Today</h1>
                            <p>Get started today and see how effortlessly <br></br>  you can plan and execute tasks with Saihatask.
                            </p>
                        </div>
                        <div id="donate1-now" class="donate-second-sec">
                            <a href="#contact-id">
                                <img src="assets/images/donate-arrow.png" alt="donate" />
                                <p>Book a Demo</p>
                            </a>
                        </div>
                    </div>
                </div>
                <div class="blog-contact-bg">
                    <div className="Blog-carousel" id="blog-id">
                        <div class="carousel-first-section">
                            <h1>
                                Insights From <br></br>
                                Blog
                            </h1>
                            <p class="first-p-acrousel">
                                Explore Updates, Tips, And<br></br> Stories on how Task management software HelpS You Plan, <br></br> Manage, And Execute

                                <br></br> Projects More Efficiently.
                            </p>
                            <div class="arrows">
                                <button id="prevArrow" class="arrow">
                                    <img src="assets/images/previous-arrow-home.png" alt="Previous Arrow" />
                                </button>
                                <button id="nextArrow" class="arrow">
                                    <img src="assets/images/next-arrow-home.png" alt="Next Arrow" />
                                </button>


                            </div>
                        </div>
                        <div className="swiper-container blogswipe overflow-hidden">
                            <div className="swiper-wrapper">
                                {[1, 2, 3, 4].map((i) => (
                                    <div className="swiper-slide" key={i}>
                                        <img
                                            src={`assets/images/${i === 1
                                                ? 'home-carousel-img2'
                                                : i === 2
                                                    ? 'home-carousel-img3'
                                                    : i === 3
                                                        ? 'home-carousel-img4'
                                                        : i === 4
                                                            ? 'home-carousel-img5'
                                                            : 'default-image'
                                                }.png`}
                                            alt="blog image"
                                            className="bolg-img img-fluid"
                                        />
                                        <div className="blog-details">
                                            <h3>
                                                {[
                                                    'Why Its Time to Stop Using Spreadsheets for Task Management',
                                                    'Top 5 Task Planning Errors Growing Teams Frequently Face',
                                                    'The Benefits of Using the Best Task Management Solution for Business ',
                                                    'Top Features to Look for in the Best Task Management Software '
                                                ][i - 1]}
                                            </h3>
                                            <p>
                                                {[
                                                    'Discover how structured task boards help teams organize work better and reduce confusion in daily operations.',
                                                    'From unclear deadlines to poor tracking—learn how to avoid the most common issues in task planning with the help of reliable task management software.',
                                                    'Discover key benefits of using the Best Task Management Solution for Business to boost team efficiency. ',
                                                    'Explore top features to find the best task management software for smarter, faster business task handling.'
                                                ][i - 1]}
                                            </p>
                                            {/* <p><a href="">Read More...</a></p> */}
                                        </div>

                                    </div>
                                ))}
                            </div>

                            {/* Optional Navigation Arrows */}
                            <div className="swiper-button-next"></div>
                            <div className="swiper-button-prev"></div>
                        </div>
                    </div>

                    <div class="contact-main-section" >
                        <div class="row align-items-center contact-section">
                            <div class="col-md-6">
                                <div class="contact-info">
                                    <div class="contact-item">
                                        <h3>Call us</h3>
                                        <p>Call our team Mon-Fri from 9am to 6pm.</p>
                                        <h3> <img src="assets/images/call.png" alt="phone icon" />
                                            <a href="tel:+919544100848">+91-95441 00848</a>
                                        </h3>
                                        <h3> <img src="assets/images/call.png" alt="phone icon" />
                                            <a href="tel:+919946000848">+91-99460 00848</a>
                                        </h3>
                                    </div>
                                    <div class="contact-item">
                                        <h3>Chat with Us</h3>
                                        <p>Speak to our friendly team via live chat.</p>
                                        <h3><img src="assets/images/chat.png" alt="message icon" /> Start a live chat</h3>
                                        <h3><img src="assets/images/email.png" alt="email icon" /><a href="mailto:business@saihasoft.com">business@saihasoft.com</a></h3>
                                    </div>
                                    <div class="contact-item">
                                        <h3>Visit Us</h3>
                                        <p class="task123">Chat to Us in person at our Saihatask HQ.</p>
                                        <h3><img src="assets/images/location.png" alt="location icon" />
                                            <a href="https://www.google.com/maps?q=1st+Floor,+Samuel+Sons+Building,+Sobha+Road,+Chakkaraparambu,+Palarivattom,+Kochi,+Ernakulam,+Kerala+682028"
                                                target="_blank" rel="noopener">
                                                1st Floor, Samuel Sons Building, Sobha Road, Chakkaraparambu, Palarivattom, Kochi, Ernakulam, Kerala 682028
                                            </a></h3>
                                    </div>
                                </div>
                            </div>
                            <div class="col-md-6 right-col-contact" id="contact-id">
                                <div class="form-section form-div">
                                    <h3>Get in Touch <br></br> <span>Have questions or need a demo?<br></br> We're here to help.</span></h3>
                                    <form name="contactForm" id="contactForm">
                                        <div className="button-sec selected-program-section">
                                            <button
                                                type="button"
                                                className="btn btn-outline-secondary btn-selected"
                                                onClick={(e) => toggleSelection(e.target)}
                                            >
                                                Demo Request
                                            </button>
                                            <button
                                                type="button"
                                                className="btn btn-outline-secondary"
                                                onClick={(e) => toggleSelection(e.target)}
                                            >
                                                Pricing Inquiry
                                            </button>
                                            {/* <button
                                                type="button"
                                                className="btn btn-outline-secondary"
                                                onClick={(e) => toggleSelection(e.target)}
                                            >
                                                Pricing Inquiry
                                            </button> */}
                                            <button
                                                type="button"
                                                className="btn btn-outline-secondary"
                                                onClick={(e) => toggleSelection(e.target)}
                                            >
                                                Integration Help
                                            </button>
                                            <button
                                                type="button"
                                                className="btn btn-outline-secondary"
                                                onClick={(e) => toggleSelection(e.target)}
                                            >
                                                Feedback
                                            </button>
                                        </div>
                                        <input type="hidden" name="selectedProgram" id="selectedProgram" value="Demo Request" />

                                        <div class="form-inputs">
                                            <input type="text" class="form-control" placeholder="Name" name="name" required
                                                id="name" />
                                            <input type="email" class="form-control" placeholder="Email" name="contactEmail"
                                                required id="contactEmail" />
                                            <div className="mobile-input-group" id="mobile-input-group">
                                                <div className={`country-code-picker${countryPickerOpen ? ' is-open' : ''}`} ref={countryPickerRef}>
                                                    <input
                                                        type="hidden"
                                                        name="countryCode"
                                                        id="countryCode"
                                                        value={`${selectedCountry.code}|${selectedCountry.iso}`}
                                                        data-code={selectedCountry.code}
                                                        data-min={selectedCountry.min}
                                                        data-max={selectedCountry.max}
                                                        data-pattern={selectedCountry.pattern}
                                                        data-message={selectedCountry.message}
                                                        required
                                                    />
                                                    <button
                                                        type="button"
                                                        className="country-code-trigger"
                                                        aria-label="Select country code"
                                                        aria-expanded={countryPickerOpen}
                                                        onClick={() => setCountryPickerOpen((open) => !open)}
                                                    >
                                                        <span className="country-code-trigger-label">
                                                            <span className="country-flag">{selectedCountry.flag}</span>
                                                            <span className="country-dial">{selectedCountry.code}</span>
                                                        </span>
                                                        <span className="country-code-chevron" aria-hidden="true"></span>
                                                    </button>
                                                    {countryPickerOpen && (
                                                        <div
                                                            className="country-code-dropdown"
                                                            data-scroll-ignore="true"
                                                            onWheel={handleDropdownScrollLock}
                                                            onTouchMove={handleDropdownScrollLock}
                                                        >
                                                            <div className="country-code-search-wrap">
                                                                <input
                                                                    ref={countrySearchRef}
                                                                    type="text"
                                                                    className="country-code-search"
                                                                    placeholder="Search country"
                                                                    value={countrySearch}
                                                                    onChange={(e) => setCountrySearch(e.target.value)}
                                                                    autoComplete="off"
                                                                />
                                                            </div>
                                                            <ul
                                                                className="country-code-list"
                                                                role="listbox"
                                                                ref={countryListRef}
                                                                data-scroll-ignore="true"
                                                            >
                                                                {filteredCountries.length > 0 ? (
                                                                    filteredCountries.map((country) => {
                                                                        const isActive = country.iso === selectedCountry.iso && country.code === selectedCountry.code;
                                                                        return (
                                                                            <li key={`${country.iso}-${country.code}`}>
                                                                                <button
                                                                                    type="button"
                                                                                    className={`country-code-option${isActive ? ' is-active' : ''}`}
                                                                                    role="option"
                                                                                    aria-selected={isActive}
                                                                                    onClick={() => handleCountrySelect(country)}
                                                                                >
                                                                                    <span className="country-flag">{country.flag}</span>
                                                                                    <span className="country-name">{country.name}</span>
                                                                                    <span className="country-dial">{country.code}</span>
                                                                                </button>
                                                                            </li>
                                                                        );
                                                                    })
                                                                ) : (
                                                                    <li className="country-code-empty">No countries found</li>
                                                                )}
                                                            </ul>
                                                        </div>
                                                    )}
                                                </div>
                                                <input
                                                    type="tel"
                                                    inputMode="numeric"
                                                    maxLength={selectedCountry.max}
                                                    className="form-control mobile-number-input"
                                                    placeholder="Mobile"
                                                    name="mobile"
                                                    required
                                                    id="mobile"
                                                    autoComplete="tel-national"
                                                    onInput={(e) => {
                                                        const max = selectedCountry.max || DEFAULT_COUNTRY.max;
                                                        e.target.value = e.target.value.replace(/\D/g, '').slice(0, max);
                                                    }}
                                                />
                                            </div>
                                            <div id="mobile-error-container" className="mobile-error-container"></div>
                                            <input type="text" class="form-control" placeholder="Company" name="company" required
                                                id="company" />
                                            <textarea class="form-control" placeholder="Message" rows="3" name="message" required
                                                id="message"></textarea>
                                        </div>
                                        <div class="capcha-center" style={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            flexDirection: "column"
                                        }}>
                                            <div class="g-recaptcha" id="recaptcha-container"
                                                data-sitekey="6Ldqro8sAAAAACTOc5sxldd5bDpK9xdFCTngef36"
                                                data-callback="recaptchaCallback">
                                            </div>
                                        </div>
                                        <div class="form-new-submit"><button class="btn-submit" type="submit">Submit</button></div>
                                    </form>
                                </div>
                            </div>
                        </div>
                        <div class="contact-line-bg">
                            <img src="assets/images/contact-form-line.png" alt="contact line" class="w-100" />
                        </div>
                    </div>
                </div>

                <footer class="footer-block">
                    <div class="footer-main">
                        <div class="container-fluid">
                            <div class="d-flex justify-content-between align-items-center footer-head-section">
                                <div class="w-100">
                                    <h4>Stay Connected with Us!</h4>
                                    <p>Join our newsletter to receive the latest updates, stories, <br></br> and opportunities to
                                        make a difference with the best task and project management software.</p>
                                </div>
                                <form name="emailForm" id="emailForm" class="w-100">
                                    <div class="input-button-footer w-100">
                                        <div class="w-100">
                                            <input type="email" placeholder="Enter Your Email" name="email" required
                                                id="email" class="w-100" />
                                        </div>
                                        <button class="btn" type="submit">Subscribe</button>
                                    </div>
                                </form>
                            </div>
                            <div class="footer-section align-items-center" id="footer-navigation">
                                <div class="logo-footer">
                                    <img src="assets/images/rays-footer-logo.png" class="footer-logo mb-4" />
                                    <p>Saihatask empowers teams to <br></br> plan, track, and complete work <br></br> efficiently through simple, smart <br></br> task management since 2022. </p>
                                </div>
                                <div class="about-rays-footer">
                                    <a href="#About-saiha">About Us</a>
                                    <a href="#program-id">Features </a>
                                    {/* <a href="#pricing">Pricing</a> */}
                                    <a href="#blog-id">Blog</a>
                                </div>
                                <div class="education-footer">
                                    <a href="#program-id">Task Boards</a>
                                    <a href="#program-id">Project Tracker</a>
                                    <a href="#program-id">Daily Review</a>
                                    <a href="#program-id">Role-Based Access</a>
                                </div>
                                <div class="volunteer-footer">
                                    <a href="#contact-id">Help Center</a>
                                    <a href="#contact-id">Request A Demo</a>
                                    <a href="#contact-id">Become A Partner</a>
                                    <a href="#contact-id">Contact Us</a>
                                    <a href="/privacy-policy">Privacy Policy</a>
                                </div>

                                <div class="social-icons-footer">
                                    <a href="https://www.facebook.com/profile.php?id=61576934409284" target="_blank">
                                        <img src="assets/images/facebook.png" alt="Twitter" class="social-icon" />
                                    </a>
                                    <a href="https://www.instagram.com/Saihatask/" target="_blank">
                                        <img src="assets/images/insta.png" alt="Instagram" class="social-icon" />
                                    </a>
                                    <a href="https://www.youtube.com/@saihatask" target="_blank">
                                        <img src="assets/images/youtube.png" alt="Youtube" class="social-icon" />
                                    </a>
                                    <a href="https://x.com/saihatask" target="_blank">
                                        <img src="assets/images/twit.png" alt="Twitter" class="social-icon" />
                                    </a>
                                </div>

                            </div>
                        </div>
                    </div>
                    <div class="copyrights text-white">
                        <div class="copyrights_div d-flex justify-content-between">
                            <div>
                                <p class="saihasoft-link">© 2026 Saihatask. All Rights Reserved.</p>
                            </div>
                            <div>
                                <a href="http://saihasoft.com/" target="_blank" class="saihasoft-link">Created by Saihasoftware
                                    technologies Pvt Ltd.</a>
                            </div>
                        </div>
                    </div>
                </footer>

            </div>
            <a href="https://api.whatsapp.com/send?phone=+919544100848&text=Hello,  I am interested in Saihatask."
                class="float" target="_blank">
                <i class="fab fa-whatsapp my-float"></i>
            </a>
            <a href="tel:+919544100848" class="tel">
                <i class="fas fa-phone my-float"></i>
            </a>
        </>
    );
};

export default Home;